/**
 * @fileoverview Export Utilities for PSP/CRE Tool v3.0 - Tab 5 Reports
 * 
 * PURPOSE:
 * Handle exporting formatted report data to various output formats:
 * - DOCX (Word documents using docx library)
 * - PDF (PDF documents using jsPDF + jspdf-autotable)
 * - XLSX (Excel spreadsheets using xlsx/SheetJS library)
 * - HTML (Styled HTML documents)
 * - Markdown (Plain text markdown)
 * - CSV (Comma-separated values for data)
 * - JSON (Raw data export)
 * 
 * DEPENDENCIES (install via npm):
 * npm install docx jspdf jspdf-autotable xlsx file-saver
 * 
 * @version 2.1
 * @author Dr. Tatar Radu MD, Romanian Military Medical Institute
 */

// ============================================================================
// LIBRARY IMPORTS
// ============================================================================

// DOCX Generation
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  Header,
  Footer,
  AlignmentType,
  WidthType,
  BorderStyle,
  PageNumber,
  HeadingLevel,
  ShadingType
} from 'docx';

// PDF Generation
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Excel Generation
import * as XLSX from 'xlsx';

// File Download Helper
import { saveAs } from 'file-saver';

// Import report constants
import { EXPORT_FORMATS } from '../constants/reportConstants.js';

// ============================================================================
// COLOR CONSTANTS FOR STATUS INDICATORS
// ============================================================================

/**
 * Color definitions for traffic light status system
 * Used consistently across all export formats
 */
const STATUS_COLORS = {
  GREEN: {
    hex: '2E7D32',
    rgb: [46, 125, 50],
    bgHex: 'C8E6C9',
    bgRgb: [200, 230, 201],
    name: 'GREEN'
  },
  AMBER: {
    hex: 'F57C00',
    rgb: [245, 124, 0],
    bgHex: 'FFE0B2',
    bgRgb: [255, 224, 178],
    name: 'AMBER'
  },
  RED: {
    hex: 'C62828',
    rgb: [198, 40, 40],
    bgHex: 'FFCDD2',
    bgRgb: [255, 205, 210],
    name: 'RED'
  },
  HEADER: {
    hex: '1976D2',
    rgb: [25, 118, 210],
    bgHex: 'BBDEFB',
    bgRgb: [187, 222, 251],
    name: 'HEADER'
  },
  WHITE: {
    hex: 'FFFFFF',
    rgb: [255, 255, 255],
    name: 'WHITE'
  },
  BLACK: {
    hex: '000000',
    rgb: [0, 0, 0],
    name: 'BLACK'
  },
  GRAY: {
    hex: '757575',
    rgb: [117, 117, 117],
    bgHex: 'F5F5F5',
    bgRgb: [245, 245, 245],
    name: 'GRAY'
  }
};

/**
 * Get color object for a status value
 * @param {string} status - 'GREEN', 'AMBER', or 'RED'
 * @returns {Object} Color object with hex and rgb values
 */
function getStatusColor(status) {
  const statusUpper = (status || '').toUpperCase();
  return STATUS_COLORS[statusUpper] || STATUS_COLORS.GRAY;
}

// ============================================================================
// BLOB CREATION AND DOWNLOAD HELPERS
// ============================================================================

/**
 * Create a Blob from data with specified MIME type
 * @param {any} data - Data to convert to Blob
 * @param {string} mimeType - MIME type for the Blob
 * @returns {Blob} Blob object ready for download
 */
function createBlob(data, mimeType) {
  return new Blob([data], { type: mimeType });
}

/**
 * Trigger browser download of a Blob using file-saver
 * @param {Blob} blob - Blob to download
 * @param {string} filename - Suggested filename for download
 */
export function downloadBlob(blob, filename) {
  saveAs(blob, filename);
  // CL-001: dev-only log removed
}

/**
 * Generate filename for report export
 * @param {Object} reportData - Report data with metadata
 * @param {string} format - Export format (docx, pdf, xlsx, etc)
 * @returns {string} Formatted filename
 */
export function generateFilename(reportData, format) {
  const reportType = reportData.metadata?.report_type || 'REPORT';
  const unit = reportData.metadata?.reporting_unit || 'UNIT';
  const date = new Date();
  const dateStr = date.toISOString().split('T')[0].replace(/-/g, '');
  const timeStr = date.toISOString().split('T')[1].slice(0, 5).replace(':', '');
  const cleanReportType = reportType.replace(/\s+/g, '_').toUpperCase();
  const cleanUnit = unit.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  const extension = EXPORT_FORMATS[format.toUpperCase()]?.extension || '.txt';
  return `${cleanReportType}_${cleanUnit}_${dateStr}_${timeStr}Z${extension}`;
}

// ============================================================================
// SAFE VALUE EXTRACTION HELPERS
// ============================================================================

/**
 * Safely extract a nested value from an object
 * @param {Object} obj - Source object
 * @param {string} path - Dot-separated path (e.g., 'a.b.c')
 * @param {*} defaultValue - Default value if path not found
 * @returns {*} Extracted value or default
 */
function safeGet(obj, path, defaultValue = null) {
  if (!obj || !path) return defaultValue;
  const parts = path.split('.');
  let current = obj;
  for (const part of parts) {
    if (current === null || current === undefined) return defaultValue;
    current = current[part];
  }
  return current !== undefined ? current : defaultValue;
}

/**
 * Format a number safely with decimal places
 * @param {number} value - Number to format
 * @param {number} decimals - Decimal places
 * @param {string} suffix - Optional suffix (e.g., '%')
 * @returns {string} Formatted string
 */
function formatNum(value, decimals = 0, suffix = '') {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return Number(value).toFixed(decimals) + suffix;
}

// BUG-6K-003: Golden hour formatting — null = PENDING (no calculation results)
// BUG-6K-006: Surgical status — now uses surgical_status field; or_tables deprecated
const fmtGoldenHour = (v) => v == null ? 'PENDING — calculation required' : `${Number(v).toFixed(0)}%`;
const goldenHourClass = (v) => v == null ? 'unknown' : v >= 80 ? 'green' : v >= 60 ? 'amber' : 'red';
const goldenHourStatus = (v) => v == null ? 'PENDING' : v >= 90 ? 'Excellent' : v >= 70 ? 'Adequate' : 'Needs Improvement';

// ============================================================================
// HTML EXPORT
// ============================================================================

/**
 * Export report as styled HTML document
 * @param {Object} reportData - Formatted report data
 * @param {Object} options - Export options
 * @returns {Blob} HTML Blob ready for download
 */
export function exportToHTML(reportData, options = {}) {
  const {
    includeStyles = true,
    includeHeader = true,
    includeFooter = true,
    printOptimized = false
  } = options;
  
  let html = '<!DOCTYPE html>\n<html lang="en">\n<head>\n';
  html += '<meta charset="UTF-8">\n';
  html += '<meta name="viewport" content="width=device-width, initial-scale=1.0">\n';
  html += `<title>${reportData.metadata?.report_type || 'Medical Report'}</title>\n`;
  
  if (includeStyles) {
    html += '<style>\n';
    html += getReportStyles(printOptimized);
    html += '</style>\n';
  }
  
  html += '</head>\n<body>\n';
  
  if (includeHeader) {
    html += generateHTMLHeader(reportData);
  }
  
  html += '<div class="report-content">\n';
  html += generateHTMLContent(reportData);
  html += '</div>\n';
  
  if (includeFooter) {
    html += generateHTMLFooter(reportData);
  }
  
  html += '</body>\n</html>';
  
  return createBlob(html, 'text/html');
}

/**
 * Generate CSS styles for HTML export
 * @private
 */
function getReportStyles(printOptimized) {
  return `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Calibri', 'Arial', sans-serif;
      font-size: ${printOptimized ? '11pt' : '12pt'};
      line-height: 1.6;
      max-width: ${printOptimized ? '100%' : '210mm'};
      margin: 0 auto;
      padding: ${printOptimized ? '10mm' : '20px'};
      background: ${printOptimized ? '#fff' : '#f5f5f5'};
    }
    .report-header {
      border-bottom: 3px solid #1976d2;
      padding-bottom: 15px;
      margin-bottom: 30px;
    }
    .report-title {
      font-size: ${printOptimized ? '16pt' : '24px'};
      font-weight: bold;
      color: #1976d2;
      margin-bottom: 10px;
    }
    .report-metadata {
      font-size: ${printOptimized ? '9pt' : '11px'};
      color: #666;
    }
    .report-metadata table { width: 100%; border-collapse: collapse; }
    .report-metadata td { padding: 3px 10px 3px 0; }
    .report-metadata td:first-child { font-weight: bold; width: 150px; }
    .section {
      margin-bottom: 30px;
      page-break-inside: avoid;
    }
    .section-title {
      font-size: ${printOptimized ? '12pt' : '16px'};
      font-weight: bold;
      color: #333;
      margin-bottom: 15px;
      padding-bottom: 5px;
      border-bottom: 2px solid #ddd;
    }
    .subsection { margin-bottom: 20px; margin-left: 20px; }
    .subsection-title {
      font-size: ${printOptimized ? '11pt' : '14px'};
      font-weight: bold;
      color: #555;
      margin-bottom: 10px;
    }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th {
      background-color: #1976d2;
      color: white;
      padding: 10px;
      text-align: left;
      font-weight: bold;
    }
    td { padding: 8px 10px; border-bottom: 1px solid #ddd; }
    tr:nth-child(even) { background-color: #f9f9f9; }
    .status-green { color: #2e7d32; font-weight: bold; }
    .status-amber { color: #f57c00; font-weight: bold; }
    .status-red { color: #c62828; font-weight: bold; }
    .metric-card {
      display: inline-block;
      width: 200px;
      padding: 15px;
      margin: 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      background: #fff;
    }
    .metric-label { font-size: ${printOptimized ? '9pt' : '11px'}; color: #666; margin-bottom: 5px; }
    .metric-value { font-size: ${printOptimized ? '18pt' : '24px'}; font-weight: bold; color: #1976d2; }
    .educational-note {
      background: #e3f2fd;
      border-left: 4px solid #1976d2;
      padding: 15px;
      margin: 20px 0;
      font-style: italic;
    }
    .report-footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 2px solid #ddd;
      font-size: ${printOptimized ? '8pt' : '10px'};
      color: #666;
      text-align: center;
    }
    @media print {
      body { background: white; margin: 0; padding: 10mm; }
      .no-print { display: none; }
      .page-break { page-break-after: always; }
    }
  `;
}

/**
 * Generate HTML header section
 * @private
 */
function generateHTMLHeader(reportData) {
  const meta = reportData.metadata || {};
  return `
    <div class="report-header">
      <div class="report-title">${meta.report_type || 'Medical Report'}</div>
      <div class="report-metadata">
        <table>
          <tr><td>Full Name:</td><td>${meta.full_name || meta.report_type || 'N/A'}</td></tr>
          <tr><td>Reporting Unit:</td><td>${meta.reporting_unit || 'N/A'}</td></tr>
          <tr><td>Date/Time Group:</td><td>${meta.dtg || new Date().toISOString()}</td></tr>
          <tr><td>Reporting Period:</td><td>${meta.reporting_period || 'N/A'}</td></tr>
          <tr><td>Classification:</td><td>${meta.classification || 'NATO UNCLASSIFIED'}</td></tr>
        </table>
      </div>
    </div>
  `;
}

/**
 * Generate HTML content from report data
 * @private
 */
function generateHTMLContent(reportData) {
  const reportType = reportData.metadata?.report_type;
  
  switch (reportType) {
    case 'MEDSITREP':
      return generateMEDSITREPHTMLContent(reportData);
    case 'CASUALTY_ESTIMATE':
      return generateCasualtyEstimateHTMLContent(reportData);
    case 'COMPONENTS_MATRIX':
      return generateComponentsMatrixHTMLContent(reportData);
    case 'NINE_LINE':
    case '9-LINE MEDEVAC':
      return generateNineLineHTMLContent(reportData);
    case 'METHANE':
      return generateMETHANEHTMLContent(reportData);
    case 'EXECUTIVE_BRIEF':
      return generateExecutiveBriefHTMLContent(reportData);
    case 'MEDASSESSREP':
      return generateMEDASSESSREPHTMLContent(reportData);
    case 'EPINATO2':
      return generateEPINATO2HTMLContent(reportData);
    case 'OOSR':
      return generateOOSRHTMLContent(reportData);
    case 'BLUE_LIGHT':
      return generateBlueLightHTMLContent(reportData);
    case 'MEDEVAC_SUMMARY':
      return generateMEDEVACSummaryHTMLContent(reportData);
    case 'NATO_PLANNING':
      return generateNATOPlanningHTMLContent(reportData);
    default:
      return generateGenericHTMLContent(reportData);
  }
}

/**
 * Generate MEDSITREP HTML content
 * @private
 */
function generateMEDSITREPHTMLContent(reportData) {
  let html = '';
  
  // Section 1: Deployed Hospital Care
  html += '<div class="section">\n<div class="section-title">1. Deployed Hospital Care</div>\n';
  
  const dhc = reportData.deployed_hospital_care || {};
  ['role2_basic', 'role2_enhanced', 'role3'].forEach(role => {
    const roleData = dhc[role];
    if (roleData) {
      const roleName = role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      html += `<div class="subsection"><div class="subsection-title">${roleName}</div>
        <table>
          <tr><th>Parameter</th><th>Value</th><th>Status</th></tr>
          <tr><td>Location</td><td>${roleData.location || 'N/A'}</td><td>-</td></tr>
          <tr><td>Beds</td><td>${roleData.beds_occupied || 0}/${roleData.beds_total || 0} (${formatNum(roleData.occupancy_rate, 0)}% occupied)</td>
            <td class="status-${(roleData.status || 'unknown').toLowerCase()}">${roleData.status || 'UNKNOWN'}</td></tr>
          <tr><td>Surgical Teams</td><td>${roleData.surgical_teams || 0}</td><td>${roleData.surgical_status || 'N/A'}</td></tr>
        </table>
      </div>\n`;
    }
  });
  html += '</div>\n';
  
  // Section 2: MEDEVAC Assets
  html += '<div class="section">\n<div class="section-title">2. MEDEVAC Assets</div>\n';
  const ma = reportData.medevac_assets || {};
  html += `<table>
    <tr><th>Parameter</th><th>Value</th><th>Status</th></tr>
    <tr><td>Availability</td><td>${ma.available || 0}/${ma.total || 0} (${formatNum(ma.availability_rate, 0)}%)</td>
      <td class="status-${(ma.status || 'unknown').toLowerCase()}">${ma.status || 'UNKNOWN'}</td></tr>
    <tr><td>Missions (24h)</td><td>${ma.missions_last_24h || 0}</td><td>${ma.avg_response_time != null ? 'Avg: ' + formatNum(ma.avg_response_time, 0) + ' min' : 'N/A'}</td></tr>
    <tr><td>Golden Hour</td><td>${fmtGoldenHour(ma.golden_hour_achievement)}</td>
      <td>${goldenHourStatus(ma.golden_hour_achievement)}</td></tr>
  </table>\n</div>\n`;
  
  // Section 3: Battle Casualties
  html += '<div class="section">\n<div class="section-title">3. Battle Casualties</div>\n';
  const bc = reportData.battle_casualties || {};
  html += `<table>
    <tr><th>Category</th><th>Count</th></tr>
    <tr><td>Total Battle Casualties</td><td><strong>${bc.total || 0}</strong></td></tr>
    <tr><td>Killed in Action (KIA)</td><td>${bc.kia || 0}</td></tr>
    <tr><td>Died of Wounds (DOW)</td><td>${bc.dow || 0}</td></tr>
    <tr><td>Wounded in Action (WIA)</td><td>${bc.wia || 0}</td></tr>
    <tr><td>Returned to Duty (RTD)</td><td>${bc.rtn || 0}</td></tr>
  </table>\n</div>\n`;
  
  // Section 4: DNBI
  html += '<div class="section">\n<div class="section-title">4. Disease and Non-Battle Injuries</div>\n';
  const dnbi = reportData.dnbi || {};
  html += `<table>
    <tr><th>Metric</th><th>Value</th></tr>
    <tr><td>Total DNBI</td><td><strong>${dnbi.total || 0}</strong></td></tr>
    <tr><td>Rate per 1000/day</td><td>${formatNum(dnbi.rate_per_1000, 2)}</td></tr>
  </table>\n</div>\n`;
  
  // Section 5: Medical Logistics
  html += '<div class="section">\n<div class="section-title">5. Medical Logistics</div>\n';
  const medlog = reportData.medlog || {};
  html += `<table>
    <tr><th>Item</th><th>Status</th><th>Assessment</th></tr>
    <tr><td>Medical materiel (AJP-4.10C)</td><td>${medlog.class_viii_days || 0} days</td>
      <td class="status-${(medlog.class_viii_status || 'unknown').toLowerCase()}">${medlog.class_viii_status || 'UNKNOWN'}</td></tr>
    <tr><td>Blood Products</td><td>${formatNum(medlog.blood_availability, 0)}%</td>
      <td class="status-${(medlog.blood_status || 'unknown').toLowerCase()}">${medlog.blood_status || 'UNKNOWN'}</td></tr>
  </table>\n</div>\n`;
  
  return html;
}

/**
 * Generate Casualty Estimate HTML content
 * @private
 */
function generateCasualtyEstimateHTMLContent(reportData) {
  let html = '';
  
  // Executive Summary
  const es = reportData.executive_summary || {};
  html += '<div class="section">\n<div class="section-title">Executive Summary</div>\n';
  html += `<p><strong>BLUF:</strong> ${es.bottom_line_up_front || 'N/A'}</p>\n`;
  html += '<div style="margin: 20px 0;">\n';
  html += `<div class="metric-card"><div class="metric-label">Total Casualties</div><div class="metric-value">${es.total_casualties || 0}</div></div>`;
  html += `<div class="metric-card"><div class="metric-label">Battle Casualties</div><div class="metric-value">${es.battle_casualties || 0}</div></div>`;
  html += `<div class="metric-card"><div class="metric-label">DNBI</div><div class="metric-value">${es.dnbi || 0}</div></div>`;
  html += `<div class="metric-card"><div class="metric-label">Medical Capacity</div><div class="metric-value status-${(es.medical_capacity_status || 'unknown').toLowerCase()}">${es.medical_capacity_status || 'UNKNOWN'}</div></div>`;
  html += '</div>\n</div>\n';
  
  // Battle Casualties
  const bc = reportData.battle_casualties || {};
  html += '<div class="section">\n<div class="section-title">Battle Casualties Analysis</div>\n';
  html += `<table>
    <tr><th>Category</th><th>Count</th><th>Rate</th></tr>
    <tr><td>Total BC</td><td><strong>${bc.total || 0}</strong></td><td>${formatNum(bc.casualty_rate, 2)}/1000/day</td></tr>
    <tr><td>KIA</td><td>${bc.kia || 0}</td><td>-</td></tr>
    <tr><td>DOW</td><td>${bc.dow || 0}</td><td>-</td></tr>
    <tr><td>WIA</td><td>${bc.wia || 0}</td><td>-</td></tr>
  </table>\n</div>\n`;
  
  // MTF Capacity
  html += '<div class="section">\n<div class="section-title">MTF Capacity Analysis</div>\n';
  const mtf = reportData.mtf_capacity || {};
  html += '<table><tr><th>Role</th><th>Beds</th><th>Occupancy</th><th>Status</th></tr>\n';
  ['role2_basic', 'role2_enhanced', 'role3'].forEach(role => {
    const data = mtf[role];
    if (data) {
      html += `<tr><td>${data.role || role}</td><td>${data.beds_occupied || 0}/${data.beds_total || 0}</td>
        <td>${formatNum(data.occupancy_rate, 0)}%</td>
        <td class="status-${(data.status || 'unknown').toLowerCase()}">${data.status || 'UNKNOWN'}</td></tr>\n`;
    }
  });
  html += '</table>\n</div>\n';
  
  return html;
}

/**
 * Generate Components Matrix HTML content
 * @private
 */
function generateComponentsMatrixHTMLContent(reportData) {
  let html = '<div class="section">\n<div class="section-title">Components of Care Status Matrix</div>\n';
  html += '<table>\n<tr><th>Component</th><th>Status</th><th>Percentage</th><th>Assessment</th></tr>\n';
  
  const components = reportData.components || {};
  const componentNames = {
    personnel: 'Medical Personnel',
    facilities: 'Medical Treatment Facilities',
    medevac: 'Medical Evacuation',
    medlog: 'Medical Logistics',
    hns: 'Host Nation Support',
    c2: 'Command & Control'
  };
  
  Object.entries(componentNames).forEach(([key, name]) => {
    const data = components[key];
    if (data) {
      html += `<tr><td><strong>${name}</strong></td>
        <td class="status-${(data.status || 'unknown').toLowerCase()}">${data.status || 'UNKNOWN'}</td>
        <td>${formatNum(data.percentage, 0)}%</td>
        <td>${data.assessment || '-'}</td></tr>\n`;
    }
  });
  
  // Overall
  const overall = components.overall;
  if (overall) {
    html += `<tr style="border-top: 3px solid #333;">
      <td><strong>OVERALL MEDICAL POSTURE</strong></td>
      <td class="status-${(overall.status || 'unknown').toLowerCase()}"><strong>${overall.status || 'UNKNOWN'}</strong></td>
      <td colspan="2"><strong>${overall.assessment || '-'}</strong></td></tr>\n`;
  }
  
  html += '</table>\n</div>\n';
  return html;
}

/**
 * Generate 9-LINE MEDEVAC HTML content
 * @private
 */
function generateNineLineHTMLContent(reportData) {
  let html = '<div class="section">\n<div class="section-title">9-LINE MEDEVAC Request</div>\n';
  html += '<table>\n<tr><th>Line</th><th>Description</th><th>Value</th></tr>\n';
  
  const lines = [
    { num: 1, desc: 'Location of Pickup Site', key: 'line_1' },
    { num: 2, desc: 'Radio Frequency', key: 'line_2' },
    { num: 3, desc: 'Number of Patients by Precedence', key: 'line_3' },
    { num: 4, desc: 'Special Equipment Required', key: 'line_4' },
    { num: 5, desc: 'Number of Patients by Type', key: 'line_5' },
    { num: 6, desc: 'Security at Pickup Site', key: 'line_6' },
    { num: 7, desc: 'Method of Marking Pickup Site', key: 'line_7' },
    { num: 8, desc: 'Patient Nationality and Status', key: 'line_8' },
    { num: 9, desc: 'NBC Contamination', key: 'line_9' }
  ];
  
  lines.forEach(line => {
    const value = reportData[line.key] || reportData.lines?.[line.key] || 'N/A';
    html += `<tr><td><strong>Line ${line.num}</strong></td><td>${line.desc}</td><td>${value}</td></tr>\n`;
  });
  
  html += '</table>\n</div>\n';
  return html;
}

/**
 * Generate METHANE HTML content
 * @private
 */
function generateMETHANEHTMLContent(reportData) {
  let html = '<div class="section">\n<div class="section-title">METHANE Report - Major Incident</div>\n';
  html += '<table>\n<tr><th>Element</th><th>Description</th><th>Details</th></tr>\n';
  
  const elements = [
    { code: 'M', desc: 'Major Incident Declaration', key: 'major_incident' },
    { code: 'E', desc: 'Exact Location', key: 'exact_location' },
    { code: 'T', desc: 'Type of Incident', key: 'type_of_incident' },
    { code: 'H', desc: 'Hazards Present', key: 'hazards' },
    { code: 'A', desc: 'Access and Egress Routes', key: 'access' },
    { code: 'N', desc: 'Number of Casualties', key: 'number_of_casualties' },
    { code: 'E', desc: 'Emergency Services Required', key: 'emergency_services' }
  ];
  
  elements.forEach(el => {
    const value = reportData[el.key] || reportData.methane?.[el.key] || 'N/A';
    html += `<tr><td><strong>${el.code}</strong></td><td>${el.desc}</td><td>${value}</td></tr>\n`;
  });
  
  html += '</table>\n</div>\n';
  return html;
}

/**
 * Generate Executive Brief HTML content
 * @private
 */
function generateExecutiveBriefHTMLContent(reportData) {
  let html = '<div class="section">\n<div class="section-title">Executive Brief</div>\n';
  
  const brief = reportData.executive_brief || reportData;
  html += `<div class="educational-note"><strong>BLUF:</strong> ${brief.bluf || brief.executive_summary?.bottom_line_up_front || 'N/A'}</div>\n`;
  
  // Key Metrics
  html += '<div style="margin: 20px 0;">\n';
  const metrics = brief.key_metrics || [];
  metrics.forEach(m => {
    html += `<div class="metric-card"><div class="metric-label">${m.label || 'Metric'}</div>
      <div class="metric-value ${m.status ? 'status-' + m.status.toLowerCase() : ''}">${m.value || 0}</div></div>`;
  });
  html += '</div>\n';
  
  // Recommendations
  if (brief.recommendations?.length > 0) {
    html += '<div class="subsection"><div class="subsection-title">Recommendations</div><ul>\n';
    brief.recommendations.forEach(r => {
      html += `<li>${r}</li>\n`;
    });
    html += '</ul></div>\n';
  }
  
  html += '</div>\n';
  return html;
}

/**
 * Generate MEDASSESSREP HTML content
 * @private
 */
function generateMEDASSESSREPHTMLContent(reportData) {
  const dhc = reportData.deployed_hospital_care || {};
  const evac = reportData.evacuation || {};
  const bc = reportData.battle_casualties || {};
  const dnbi = reportData.dnbi || {};
  
  let html = '<div class="section">\n<div class="section-title">MEDASSESSREP - Medical Assessment Report</div>\n';
  html += `<p><strong>Assessment Period:</strong> ${reportData.metadata?.reporting_period || 'Weekly'}</p>\n`;
  
  // Medical Situation Assessment
  html += '<div class="subsection">\n<h3>1. Medical Situation Assessment</h3>\n';
  html += '<table class="data-table">\n<tr><th>Metric</th><th>Current</th><th>Trend</th><th>Status</th></tr>\n';
  html += `<tr><td>Battle Casualties</td><td>${bc.total || 0}</td><td>${bc.trend || '→'}</td><td class="status-${(bc.status || 'GREEN').toLowerCase()}">${bc.status || 'GREEN'}</td></tr>\n`;
  html += `<tr><td>DNBI</td><td>${dnbi.total || 0}</td><td>${dnbi.trend || '→'}</td><td class="status-${(dnbi.status || 'GREEN').toLowerCase()}">${dnbi.status || 'GREEN'}</td></tr>\n`;
  html += `<tr><td>DOW Rate</td><td>${evac.dow_rate || 0}%</td><td>${evac.dow_trend || '→'}</td><td class="status-${(evac.dow_status || 'GREEN').toLowerCase()}">${evac.dow_status || 'GREEN'}</td></tr>\n`;
  html += '</table>\n</div>\n';
  
  // MTF Status
  html += '<div class="subsection">\n<h3>2. MTF Status</h3>\n';
  html += '<table class="data-table">\n<tr><th>Facility</th><th>Beds</th><th>Occupied</th><th>Available</th><th>Status</th></tr>\n';
  ['role2_basic', 'role2_enhanced', 'role3'].forEach(role => {
    const r = dhc[role];
    if (r) {
      const name = role.replace(/_/g, ' ').toUpperCase();
      html += `<tr><td>${name}</td><td>${r.beds_total || 0}</td><td>${r.beds_occupied || 0}</td><td>${(r.beds_total || 0) - (r.beds_occupied || 0)}</td><td class="status-${(r.status || 'GREEN').toLowerCase()}">${r.status || 'GREEN'}</td></tr>\n`;
    }
  });
  html += '</table>\n</div>\n';
  
  // Golden Hour
  html += '<div class="subsection">\n<h3>3. Evacuation Assessment</h3>\n';
  html += `<p><strong>Golden Hour Achievement:</strong> <span class="status-${goldenHourClass(evac.golden_hour_achievement)}">${fmtGoldenHour(evac.golden_hour_achievement)}</span></p>\n`;
  html += `<p><strong>MEDEVAC Availability:</strong> ${evac.effective_availability?.percentage || 0}%</p>\n`;
  html += '</div>\n</div>\n';
  return html;
}

/**
 * Generate EPINATO2 HTML content
 * @private
 */
function generateEPINATO2HTMLContent(reportData) {
  const dnbi = reportData.dnbi || {};
  const surveillance = reportData.disease_surveillance || {};
  
  let html = '<div class="section">\n<div class="section-title">EPINATO2 - Disease/Injury Surveillance Report</div>\n';
  html += '<p><em>IAW STANAG 2535 - Medical Surveillance</em></p>\n';
  html += `<p><strong>Reporting Period:</strong> ${reportData.metadata?.reporting_period || 'Weekly'}</p>\n`;
  
  // DNBI Summary
  html += '<div class="subsection">\n<h3>Section A: DNBI Summary</h3>\n';
  html += '<table class="data-table">\n<tr><th>Category</th><th>Cases</th><th>Rate/1000/Week</th><th>Status</th></tr>\n';
  html += `<tr><td>Total DNBI</td><td>${dnbi.total || 0}</td><td>${dnbi.rate_per_1000 || 0}</td><td class="status-${(dnbi.status || 'GREEN').toLowerCase()}">${dnbi.status || 'GREEN'}</td></tr>\n`;
  const categories = dnbi.by_category || {};
  Object.entries(categories).forEach(([cat, data]) => {
    html += `<tr><td>${cat}</td><td>${data.cases || 0}</td><td>${data.rate || 0}</td><td class="status-${(data.status || 'GREEN').toLowerCase()}">${data.status || 'GREEN'}</td></tr>\n`;
  });
  html += '</table>\n</div>\n';
  
  // Disease Surveillance
  html += '<div class="subsection">\n<h3>Section B: Disease Surveillance</h3>\n';
  html += '<table class="data-table">\n<tr><th>Disease</th><th>ICD Code</th><th>Cases</th><th>Alert</th></tr>\n';
  const diseases = surveillance.diseases || [];
  if (diseases.length > 0) {
    diseases.forEach(d => {
      html += `<tr><td>${d.name}</td><td>${d.icd_code || '-'}</td><td>${d.cases}</td><td class="status-${(d.alert_level || 'GREEN').toLowerCase()}">${d.alert_level || 'NORMAL'}</td></tr>\n`;
    });
  } else {
    html += '<tr><td colspan="4">No reportable diseases this period</td></tr>\n';
  }
  html += '</table>\n</div>\n';
  
  // Environmental
  html += '<div class="subsection">\n<h3>Section C: Environmental Factors</h3>\n';
  const env = reportData.environmental_factors || {};
  html += `<p>Heat Casualties: ${env.heat_casualties || 0} | Cold Injuries: ${env.cold_injuries || 0} | Water Quality: ${env.water_quality || 'SATISFACTORY'}</p>\n`;
  html += '</div>\n</div>\n';
  return html;
}

/**
 * Generate OOSR HTML content
 * @private
 */
function generateOOSRHTMLContent(reportData) {
  const items = reportData.out_of_standards?.items || [];
  
  let html = '<div class="section">\n<div class="section-title">OUT OF STANDARDS REPORT (OOSR)</div>\n';
  html += '<p><em>Items not meeting NATO medical standards requiring command attention</em></p>\n';
  
  // Summary
  html += '<div class="subsection">\n<h3>Summary</h3>\n';
  html += '<table class="data-table">\n';
  html += `<tr><td>Total Items Out of Standard</td><td class="status-${items.length > 5 ? 'red' : items.length > 2 ? 'amber' : 'green'}">${items.length}</td></tr>\n`;
  html += `<tr><td>Critical</td><td class="status-red">${items.filter(i => i.severity === 'CRITICAL').length}</td></tr>\n`;
  html += `<tr><td>Major</td><td class="status-amber">${items.filter(i => i.severity === 'MAJOR').length}</td></tr>\n`;
  html += `<tr><td>Minor</td><td>${items.filter(i => i.severity === 'MINOR').length}</td></tr>\n`;
  html += '</table>\n</div>\n';
  
  if (items.length > 0) {
    html += '<div class="subsection">\n<h3>Detailed Items</h3>\n';
    html += '<table class="data-table">\n<tr><th>Item</th><th>Standard</th><th>Actual</th><th>Severity</th><th>Action</th></tr>\n';
    items.forEach(item => {
      html += `<tr><td>${item.item}</td><td>${item.standard}</td><td>${item.actual}</td><td class="status-${item.severity === 'CRITICAL' ? 'red' : item.severity === 'MAJOR' ? 'amber' : 'green'}">${item.severity}</td><td>${item.action || 'Review'}</td></tr>\n`;
    });
    html += '</table>\n</div>\n';
  } else {
    html += '<div class="subsection">\n<p class="status-green"><strong>✓ All items within NATO standards</strong></p>\n</div>\n';
  }
  html += '</div>\n';
  return html;
}

/**
 * Generate Blue Light HTML content
 * @private
 */
function generateBlueLightHTMLContent(reportData) {
  const blueLight = reportData.blue_light || {};
  const capacity = blueLight.capacity || {};
  const recommendations = blueLight.recommendations || [];
  
  let html = '<div class="section">\n<div class="section-title">BLUE LIGHT PROTOCOL REPORT</div>\n';
  html += '<p><em>Medical surge capacity recommendations when standards cannot be met</em></p>\n';
  
  // Status
  html += '<div class="subsection">\n<h3>Current Status</h3>\n';
  html += '<table class="data-table">\n';
  html += `<tr><td>Blue Light Status</td><td class="status-${blueLight.activated ? 'red' : 'green'}">${blueLight.activated ? 'ACTIVATED' : 'NOT ACTIVATED'}</td></tr>\n`;
  html += `<tr><td>Trigger Reason</td><td>${blueLight.trigger_reason || 'N/A'}</td></tr>\n`;
  html += `<tr><td>Expected Duration</td><td>${blueLight.expected_duration || 'N/A'}</td></tr>\n`;
  html += '</table>\n</div>\n';
  
  // Capacity Assessment
  html += '<div class="subsection">\n<h3>Capacity Assessment</h3>\n';
  html += '<table class="data-table">\n<tr><th>Resource</th><th>Required</th><th>Available</th><th>Gap</th><th>Status</th></tr>\n';
  html += `<tr><td>Surgical Teams</td><td>${capacity.surgical_required || 0}</td><td>${capacity.surgical_available || 0}</td><td>${(capacity.surgical_required || 0) - (capacity.surgical_available || 0)}</td><td class="status-${(capacity.surgical_status || 'GREEN').toLowerCase()}">${capacity.surgical_status || 'GREEN'}</td></tr>\n`;
  html += `<tr><td>ICU Beds</td><td>${capacity.icu_required || 0}</td><td>${capacity.icu_available || 0}</td><td>${(capacity.icu_required || 0) - (capacity.icu_available || 0)}</td><td class="status-${(capacity.icu_status || 'GREEN').toLowerCase()}">${capacity.icu_status || 'GREEN'}</td></tr>\n`;
  html += `<tr><td>Blood Units</td><td>${capacity.blood_required || 0}</td><td>${capacity.blood_available || 0}</td><td>${(capacity.blood_required || 0) - (capacity.blood_available || 0)}</td><td class="status-${(capacity.blood_status || 'GREEN').toLowerCase()}">${capacity.blood_status || 'GREEN'}</td></tr>\n`;
  html += `<tr><td>MEDEVAC Sorties</td><td>${capacity.medevac_required || 0}</td><td>${capacity.medevac_available || 0}</td><td>${(capacity.medevac_required || 0) - (capacity.medevac_available || 0)}</td><td class="status-${(capacity.medevac_status || 'GREEN').toLowerCase()}">${capacity.medevac_status || 'GREEN'}</td></tr>\n`;
  html += '</table>\n</div>\n';
  
  // Recommendations
  if (recommendations.length > 0) {
    html += '<div class="subsection">\n<h3>Recommendations</h3>\n<ol>\n';
    recommendations.forEach(rec => {
      html += `<li><strong>${rec.action || rec}</strong>: ${rec.details || ''} <em>(Priority: ${rec.priority || 'MEDIUM'})</em></li>\n`;
    });
    html += '</ol>\n</div>\n';
  }
  html += '</div>\n';
  return html;
}

/**
 * Generate MEDEVAC Summary HTML content
 * @private
 */
function generateMEDEVACSummaryHTMLContent(reportData) {
  const medevac = reportData.medevac_assets || {};
  const missions = reportData.medevac_missions || {};
  const helicopters = medevac.helicopters || medevac;
  const ground = medevac.ground_ambulances || {};
  
  let html = '<div class="section">\n<div class="section-title">MEDEVAC SUMMARY REPORT</div>\n';
  
  // Asset Status
  html += '<div class="subsection">\n<h3>Asset Availability</h3>\n';
  html += '<table class="data-table">\n<tr><th>Asset</th><th>Total</th><th>Available</th><th>On Mission</th><th>Status</th></tr>\n';
  html += `<tr><td>MEDEVAC Helicopters</td><td>${helicopters.total || 0}</td><td>${helicopters.available || 0}</td><td>${helicopters.on_mission || 0}</td><td class="status-${(medevac.status || 'GREEN').toLowerCase()}">${medevac.status || 'GREEN'}</td></tr>\n`;
  html += `<tr><td>Ground Ambulances</td><td>${ground.total || 0}</td><td>${ground.available || 0}</td><td>${ground.on_mission || 0}</td><td class="status-${(ground.status || 'GREEN').toLowerCase()}">${ground.status || 'GREEN'}</td></tr>\n`;
  html += '</table>\n</div>\n';
  
  // Mission Statistics
  html += '<div class="subsection">\n<h3>Mission Statistics (24h)</h3>\n';
  html += '<table class="data-table">\n';
  html += `<tr><td>Total Missions</td><td>${missions.total || medevac.missions_last_24h || 0}</td></tr>\n`;
  html += `<tr><td>Patients Evacuated</td><td>${missions.patients_evacuated || 0}</td></tr>\n`;
  const _avgRT = missions.avg_response_time ?? medevac.avg_response_time ?? null;
  html += `<tr><td>Avg Response Time</td><td>${_avgRT != null ? _avgRT + ' min' : 'N/A'}</td></tr>\n`;
  const ghPct = missions.golden_hour_pct ?? medevac.golden_hour_achievement ?? null;
  html += `<tr><td>Golden Hour Achievement</td><td class="status-${goldenHourClass(ghPct)}">${fmtGoldenHour(ghPct)}</td></tr>\n`;
  html += '</table>\n</div>\n';
  
  // Routes
  const routes = medevac.routes || [];
  if (routes.length > 0) {
    html += '<div class="subsection">\n<h3>Evacuation Routes</h3>\n';
    html += '<table class="data-table">\n<tr><th>Route</th><th>From</th><th>To</th><th>Distance</th><th>Status</th></tr>\n';
    routes.forEach(r => {
      html += `<tr><td>${r.name}</td><td>${r.from}</td><td>${r.to}</td><td>${r.distance} km</td><td class="status-${(r.status || 'GREEN').toLowerCase()}">${r.status || 'OPEN'}</td></tr>\n`;
    });
    html += '</table>\n</div>\n';
  }
  html += '</div>\n';
  return html;
}

/**
 * Generate NATO Planning Format HTML content
 * @private
 */
function generateNATOPlanningHTMLContent(reportData) {
  const exec = reportData.executive_summary || {};
  const bc = reportData.battle_casualties || {};
  const components = reportData.components || {};
  
  let html = '<div class="section">\n<div class="section-title">NATO MEDICAL PLANNING FORMAT</div>\n';
  html += '<p><em>IAW AJMedP-5 Allied Joint Medical Planning Doctrine</em></p>\n';
  
  // Annex Q Header
  html += '<div class="subsection">\n<h3>ANNEX Q - MEDICAL ESTIMATE</h3>\n';
  
  // Part 1: Mission
  html += '<h4>Part 1: Mission Analysis</h4>\n';
  html += `<p><strong>Operation:</strong> ${reportData.metadata?.reporting_unit || 'OPERATION'}</p>\n`;
  html += `<p><strong>Duration:</strong> ${reportData.metadata?.reporting_period || 'N/A'}</p>\n`;
  html += `<p><strong>Force Size:</strong> ${exec.force_size || 'N/A'} personnel</p>\n`;
  
  // Part 2: Casualty Estimate
  html += '<h4>Part 2: Casualty Estimate</h4>\n';
  html += '<table class="data-table">\n<tr><th>Category</th><th>Estimate</th><th>Daily Rate</th></tr>\n';
  html += `<tr><td>Battle Casualties</td><td>${bc.total || 0}</td><td>${bc.daily_rate || 0}</td></tr>\n`;
  html += `<tr><td>- KIA</td><td>${bc.kia || 0}</td><td>-</td></tr>\n`;
  html += `<tr><td>- WIA</td><td>${bc.wia || 0}</td><td>-</td></tr>\n`;
  html += `<tr><td>- DOW</td><td>${bc.dow || 0}</td><td>-</td></tr>\n`;
  html += `<tr><td>DNBI</td><td>${reportData.dnbi?.total || 0}</td><td>${reportData.dnbi?.daily_rate || 0}</td></tr>\n`;
  html += `<tr><td><strong>TOTAL</strong></td><td><strong>${exec.total_casualties || 0}</strong></td><td>-</td></tr>\n`;
  html += '</table>\n';
  
  // Part 3: Medical Support Concept
  html += '<h4>Part 3: Medical Support Concept</h4>\n';
  html += '<table class="data-table">\n<tr><th>Component</th><th>Status</th><th>Assessment</th></tr>\n';
  html += `<tr><td>Medical Treatment</td><td class="status-${(components.facilities?.status || 'GREEN').toLowerCase()}">${components.facilities?.status || 'GREEN'}</td><td>${components.facilities?.assessment || 'Adequate'}</td></tr>\n`;
  html += `<tr><td>Medical Evacuation</td><td class="status-${(components.medevac?.status || 'GREEN').toLowerCase()}">${components.medevac?.status || 'GREEN'}</td><td>${components.medevac?.assessment || 'Adequate'}</td></tr>\n`;
  html += `<tr><td>Medical Logistics</td><td class="status-${(components.medlog?.status || 'GREEN').toLowerCase()}">${components.medlog?.status || 'GREEN'}</td><td>${components.medlog?.assessment || 'Adequate'}</td></tr>\n`;
  html += `<tr><td>Medical Personnel</td><td class="status-${(components.personnel?.status || 'GREEN').toLowerCase()}">${components.personnel?.status || 'GREEN'}</td><td>${components.personnel?.assessment || 'Adequate'}</td></tr>\n`;
  html += '</table>\n';
  
  // Part 4: Conclusions
  html += '<h4>Part 4: Conclusions</h4>\n';
  html += `<p><strong>Overall Supportability:</strong> <span class="status-${(components.overall?.status || 'GREEN').toLowerCase()}">${components.overall?.status || 'GREEN'}</span></p>\n`;
  html += `<p>${components.overall?.assessment || 'Medical support is adequate for planned operations.'}</p>\n`;
  
  html += '</div>\n</div>\n';
  return html;
}

/**
 * Generate generic HTML content for unknown report types
 * @private
 */
function generateGenericHTMLContent(reportData) {
  return `
    <div class="section">
      <div class="section-title">Report Data</div>
      <pre style="background: #f5f5f5; padding: 15px; overflow-x: auto; font-size: 11px;">
${JSON.stringify(reportData, null, 2)}
      </pre>
    </div>
  `;
}

/**
 * Generate HTML footer section
 * @private
 */
function generateHTMLFooter(reportData) {
  return `
    <div class="report-footer">
      <p>Generated by PSP/CRE Tool v3.0 - Educational Military Medical Planning Tool</p>
      <p>Dr. Tatar Radu MD, Romanian Military Medical Institute</p>
      <p>${new Date().toISOString()}</p>
      <p>${reportData.metadata?.classification || 'NATO UNCLASSIFIED'}</p>
    </div>
  `;
}

// ============================================================================
// MARKDOWN EXPORT
// ============================================================================

/**
 * Export report as Markdown document
 * @param {Object} reportData - Formatted report data
 * @param {Object} options - Export options
 * @returns {Blob} Markdown Blob ready for download
 */
export function exportToMarkdown(reportData, options = {}) {
  let md = '';
  
  md += `# ${reportData.metadata?.report_type || 'Medical Report'}\n\n`;
  md += `**${reportData.metadata?.full_name || ''}**\n\n`;
  md += `---\n\n`;
  
  md += `- **Reporting Unit**: ${reportData.metadata?.reporting_unit || 'N/A'}\n`;
  md += `- **Date/Time**: ${reportData.metadata?.dtg || new Date().toISOString()}\n`;
  md += `- **Period**: ${reportData.metadata?.reporting_period || 'N/A'}\n`;
  md += `- **Classification**: ${reportData.metadata?.classification || 'NATO UNCLASSIFIED'}\n\n`;
  md += `---\n\n`;
  
  // Report-specific content
  const reportType = reportData.metadata?.report_type;
  
  if (reportType === 'MEDSITREP') {
    md += generateMEDSITREPMarkdown(reportData);
  } else if (reportType === 'CASUALTY_ESTIMATE') {
    md += generateCasualtyEstimateMarkdown(reportData);
  } else if (reportType === 'COMPONENTS_MATRIX') {
    md += generateComponentsMatrixMarkdown(reportData);
  } else {
    md += `## Report Data\n\n\`\`\`json\n${JSON.stringify(reportData, null, 2)}\n\`\`\`\n\n`;
  }
  
  md += `---\n\n`;
  md += `*Generated by PSP/CRE Tool v3.0*\n`;
  md += `*${new Date().toISOString()}*\n`;
  
  return createBlob(md, 'text/markdown');
}

function generateMEDSITREPMarkdown(reportData) {
  let md = '';
  
  md += '## 1. Deployed Hospital Care\n\n';
  const dhc = reportData.deployed_hospital_care || {};
  ['role2_basic', 'role2_enhanced', 'role3'].forEach(role => {
    const data = dhc[role];
    if (data) {
      const name = role.replace(/_/g, ' ').toUpperCase();
      md += `### ${name}\n\n`;
      md += `| Parameter | Value | Status |\n`;
      md += `|-----------|-------|--------|\n`;
      md += `| Location | ${data.location || 'N/A'} | - |\n`;
      md += `| Beds | ${data.beds_occupied || 0}/${data.beds_total || 0} | **${data.status || 'UNKNOWN'}** |\n`;
      md += `| Surgical Teams | ${data.surgical_teams || 0} | ${data.surgical_status || 'N/A'} |\n\n`;
    }
  });
  
  md += '## 2. MEDEVAC Assets\n\n';
  const ma = reportData.medevac_assets || {};
  md += `| Metric | Value |\n|--------|-------|\n`;
  md += `| Availability | ${ma.available || 0}/${ma.total || 0} (${formatNum(ma.availability_rate, 0)}%) |\n`;
  md += `| Status | **${ma.status || 'UNKNOWN'}** |\n`;
  md += `| Golden Hour | ${fmtGoldenHour(ma.golden_hour_achievement)} |\n\n`;
  
  md += '## 3. Battle Casualties\n\n';
  const bc = reportData.battle_casualties || {};
  md += `| Category | Count |\n|----------|-------|\n`;
  md += `| **Total BC** | **${bc.total || 0}** |\n`;
  md += `| KIA | ${bc.kia || 0} |\n`;
  md += `| DOW | ${bc.dow || 0} |\n`;
  md += `| WIA | ${bc.wia || 0} |\n\n`;
  
  return md;
}

function generateCasualtyEstimateMarkdown(reportData) {
  let md = '';
  const es = reportData.executive_summary || {};
  
  md += '## Executive Summary\n\n';
  md += `> **BLUF:** ${es.bottom_line_up_front || 'N/A'}\n\n`;
  md += `| Metric | Value |\n|--------|-------|\n`;
  md += `| Total Casualties | **${es.total_casualties || 0}** |\n`;
  md += `| Battle Casualties | ${es.battle_casualties || 0} |\n`;
  md += `| DNBI | ${es.dnbi || 0} |\n`;
  md += `| Medical Capacity | **${es.medical_capacity_status || 'UNKNOWN'}** |\n\n`;
  
  return md;
}

function generateComponentsMatrixMarkdown(reportData) {
  let md = '## Components Status\n\n';
  md += `| Component | Status | % | Assessment |\n`;
  md += `|-----------|--------|---|------------|\n`;
  
  const components = reportData.components || {};
  const names = {
    personnel: 'Medical Personnel',
    facilities: 'MTF',
    medevac: 'MEDEVAC',
    medlog: 'Medical Logistics',
    hns: 'Host Nation Support',
    c2: 'Command & Control'
  };
  
  Object.entries(names).forEach(([key, name]) => {
    const data = components[key];
    if (data) {
      md += `| ${name} | **${data.status || 'UNKNOWN'}** | ${formatNum(data.percentage, 0)}% | ${data.assessment || '-'} |\n`;
    }
  });
  
  const overall = components.overall;
  if (overall) {
    md += `| **OVERALL** | **${overall.status || 'UNKNOWN'}** | - | ${overall.assessment || '-'} |\n`;
  }
  
  return md + '\n';
}

// ============================================================================
// CSV EXPORT
// ============================================================================

/**
 * Export report data as CSV
 * @param {Object} reportData - Formatted report data
 * @param {Object} options - Export options
 * @returns {Blob} CSV Blob ready for download
 */
export function exportToCSV(reportData, options = {}) {
  const reportType = reportData.metadata?.report_type;
  let csv = '';
  
  if (reportType === 'MEDSITREP') {
    csv = generateMEDSITREPCSV(reportData);
  } else if (reportType === 'CASUALTY_ESTIMATE') {
    csv = generateCasualtyEstimateCSV(reportData);
  } else if (reportType === 'COMPONENTS_MATRIX') {
    csv = generateComponentsMatrixCSV(reportData);
  } else {
    csv = 'Field,Value\n';
    const meta = reportData.metadata || {};
    Object.entries(meta).forEach(([key, value]) => {
      csv += `"${key}","${value}"\n`;
    });
  }
  
  csv += `\n"Generated","${new Date().toISOString()}"\n`;
  csv += '"Tool","PSP/CRE Tool v3.0"\n';
  
  return createBlob(csv, 'text/csv');
}

function generateMEDSITREPCSV(reportData) {
  let csv = 'Section,Parameter,Value,Status\n';
  
  const dhc = reportData.deployed_hospital_care || {};
  ['role2_basic', 'role2_enhanced', 'role3'].forEach(role => {
    const data = dhc[role];
    if (data) {
      csv += `"${role}","Beds Total","${data.beds_total || 0}","${data.status || 'UNKNOWN'}"\n`;
      csv += `"${role}","Beds Occupied","${data.beds_occupied || 0}",""\n`;
      csv += `"${role}","Occupancy Rate","${formatNum(data.occupancy_rate, 1)}%",""\n`;
    }
  });
  
  const ma = reportData.medevac_assets || {};
  csv += `"MEDEVAC","Available","${ma.available || 0}","${ma.status || 'UNKNOWN'}"\n`;
  csv += `"MEDEVAC","Total","${ma.total || 0}",""\n`;
  csv += `"MEDEVAC","Golden Hour","${fmtGoldenHour(ma.golden_hour_achievement)}",""\n`;
  
  const bc = reportData.battle_casualties || {};
  csv += `"Battle Casualties","Total","${bc.total || 0}",""\n`;
  csv += `"Battle Casualties","KIA","${bc.kia || 0}",""\n`;
  csv += `"Battle Casualties","DOW","${bc.dow || 0}",""\n`;
  csv += `"Battle Casualties","WIA","${bc.wia || 0}",""\n`;
  
  return csv;
}

function generateCasualtyEstimateCSV(reportData) {
  let csv = 'Category,Metric,Value\n';
  
  const es = reportData.executive_summary || {};
  csv += `"Executive Summary","Total Casualties","${es.total_casualties || 0}"\n`;
  csv += `"Executive Summary","Battle Casualties","${es.battle_casualties || 0}"\n`;
  csv += `"Executive Summary","DNBI","${es.dnbi || 0}"\n`;
  csv += `"Executive Summary","Medical Capacity Status","${es.medical_capacity_status || 'UNKNOWN'}"\n`;
  
  const bc = reportData.battle_casualties || {};
  csv += `"Battle Casualties","Total","${bc.total || 0}"\n`;
  csv += `"Battle Casualties","KIA","${bc.kia || 0}"\n`;
  csv += `"Battle Casualties","DOW","${bc.dow || 0}"\n`;
  csv += `"Battle Casualties","WIA","${bc.wia || 0}"\n`;
  csv += `"Battle Casualties","Rate per 1000/day","${formatNum(bc.casualty_rate, 2)}"\n`;
  
  return csv;
}

function generateComponentsMatrixCSV(reportData) {
  let csv = 'Component,Status,Percentage,Assessment\n';
  
  const components = reportData.components || {};
  const names = {
    personnel: 'Medical Personnel',
    facilities: 'MTF',
    medevac: 'MEDEVAC',
    medlog: 'Medical Logistics',
    hns: 'Host Nation Support',
    c2: 'Command & Control',
    overall: 'OVERALL'
  };
  
  Object.entries(names).forEach(([key, name]) => {
    const data = components[key];
    if (data) {
      csv += `"${name}","${data.status || 'UNKNOWN'}","${formatNum(data.percentage, 0)}","${data.assessment || ''}"\n`;
    }
  });
  
  return csv;
}

// ============================================================================
// JSON EXPORT
// ============================================================================

/**
 * Export report as JSON data
 * @param {Object} reportData - Formatted report data
 * @param {Object} options - Export options
 * @returns {Blob} JSON Blob ready for download
 */
export function exportToJSON(reportData, options = {}) {
  const { pretty = true } = options;
  const json = pretty 
    ? JSON.stringify(reportData, null, 2)
    : JSON.stringify(reportData);
  return createBlob(json, 'application/json');
}

// ============================================================================
// DOCX EXPORT - FULL IMPLEMENTATION
// ============================================================================

/**
 * Export report as DOCX (Word document)
 * 
 * @param {Object} reportData - Formatted report data
 * @param {Object} options - Export options
 * @returns {Promise<Blob>} DOCX Blob ready for download
 */
export async function exportToDOCX(reportData, options = {}) {
  try {
    // Define consistent border style
    const tableBorder = { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' };
    const cellBorders = { top: tableBorder, bottom: tableBorder, left: tableBorder, right: tableBorder };
    
    // Build document sections based on report type
    const reportType = reportData.metadata?.report_type;
    let contentSections = [];
    
    switch (reportType) {
      case 'MEDSITREP':
        contentSections = generateMEDSITREPDOCX(reportData, cellBorders);
        break;
      case 'CASUALTY_ESTIMATE':
        contentSections = generateCasualtyEstimateDOCX(reportData, cellBorders);
        break;
      case 'COMPONENTS_MATRIX':
        contentSections = generateComponentsMatrixDOCX(reportData, cellBorders);
        break;
      case 'NINE_LINE':
      case '9-LINE MEDEVAC':
        contentSections = generateNineLineDOCX(reportData, cellBorders);
        break;
      case 'METHANE':
        contentSections = generateMETHANEDOCX(reportData, cellBorders);
        break;
      case 'EXECUTIVE_BRIEF':
        contentSections = generateExecutiveBriefDOCX(reportData, cellBorders);
        break;
      case 'MEDASSESSREP':
        contentSections = generateMEDASSESSREPDOCX(reportData, cellBorders);
        break;
      case 'EPINATO2':
        contentSections = generateEPINATO2DOCX(reportData, cellBorders);
        break;
      case 'OOSR':
        contentSections = generateOOSRDOCX(reportData, cellBorders);
        break;
      case 'BLUE_LIGHT':
        contentSections = generateBlueLightDOCX(reportData, cellBorders);
        break;
      case 'MEDEVAC_SUMMARY':
        contentSections = generateMEDEVACSummaryDOCX(reportData, cellBorders);
        break;
      case 'NATO_PLANNING':
        contentSections = generateNATOPlanningDOCX(reportData, cellBorders);
        break;
      default:
        contentSections = generateGenericDOCX(reportData, cellBorders);
    }
    
    // Create the document
    const doc = new Document({
      styles: {
        default: {
          document: {
            run: { font: 'Arial', size: 22 } // 11pt
          }
        },
        paragraphStyles: [
          {
            id: 'Title',
            name: 'Title',
            basedOn: 'Normal',
            run: { size: 48, bold: true, color: '1976D2', font: 'Arial' },
            paragraph: { spacing: { before: 240, after: 120 }, alignment: AlignmentType.CENTER }
          },
          {
            id: 'Heading1',
            name: 'Heading 1',
            basedOn: 'Normal',
            run: { size: 28, bold: true, color: '333333', font: 'Arial' },
            paragraph: { spacing: { before: 400, after: 200 } }
          },
          {
            id: 'Heading2',
            name: 'Heading 2',
            basedOn: 'Normal',
            run: { size: 24, bold: true, color: '555555', font: 'Arial' },
            paragraph: { spacing: { before: 300, after: 150 } }
          }
        ]
      },
      sections: [{
        properties: {
          page: {
            margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 }
          }
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: reportData.metadata?.classification || 'NATO UNCLASSIFIED',
                    size: 18,
                    color: '666666'
                  })
                ]
              })
            ]
          })
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({ text: 'PSP/CRE Tool v3.0 | Page ', size: 16, color: '666666' }),
                  new TextRun({ children: [PageNumber.CURRENT], size: 16, color: '666666' }),
                  new TextRun({ text: ' of ', size: 16, color: '666666' }),
                  new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 16, color: '666666' })
                ]
              })
            ]
          })
        },
        children: [
          // Title
          new Paragraph({
            heading: HeadingLevel.TITLE,
            children: [new TextRun({ text: reportData.metadata?.report_type || 'Medical Report', bold: true })]
          }),
          // Subtitle
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
            children: [new TextRun({ text: reportData.metadata?.full_name || '', size: 24, italics: true, color: '666666' })]
          }),
          // Metadata table
          createMetadataTableDOCX(reportData, cellBorders),
          // Spacer
          new Paragraph({ spacing: { after: 400 }, children: [] }),
          // Content sections
          ...contentSections,
          // Footer info
          new Paragraph({
            spacing: { before: 600 },
            children: [
              new TextRun({ text: `Generated: ${new Date().toISOString()}`, size: 18, color: '999999' })
            ]
          })
        ]
      }]
    });
    
    // Generate blob using Packer
    const blob = await Packer.toBlob(doc);
    // CL-001: dev-only log removed
    return blob;
    
  } catch (error) {
    console.error('❌ DOCX export failed:', error);
    throw new Error(`Failed to generate DOCX: ${error.message}`);
  }
}

/**
 * Create metadata table for DOCX
 * @private
 */
function createMetadataTableDOCX(reportData, cellBorders) {
  const meta = reportData.metadata || {};
  
  const rows = [
    ['Reporting Unit', meta.reporting_unit || 'N/A'],
    ['Date/Time Group', meta.dtg || new Date().toISOString()],
    ['Reporting Period', meta.reporting_period || 'N/A'],
    ['Classification', meta.classification || 'NATO UNCLASSIFIED']
  ];
  
  return new Table({
    columnWidths: [3000, 6360],
    rows: rows.map(([label, value]) => 
      new TableRow({
        children: [
          new TableCell({
            borders: cellBorders,
            width: { size: 3000, type: WidthType.DXA },
            children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, size: 20 })] })]
          }),
          new TableCell({
            borders: cellBorders,
            width: { size: 6360, type: WidthType.DXA },
            children: [new Paragraph({ children: [new TextRun({ text: String(value), size: 20 })] })]
          })
        ]
      })
    )
  });
}

/**
 * Generate MEDSITREP content for DOCX
 * @private
 */
function generateMEDSITREPDOCX(reportData, cellBorders) {
  const sections = [];
  
  // Section 1: Deployed Hospital Care
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: '1. Deployed Hospital Care' })]
    })
  );
  
  const dhc = reportData.deployed_hospital_care || {};
  ['role2_basic', 'role2_enhanced', 'role3'].forEach(role => {
    const data = dhc[role];
    if (data) {
      const roleName = role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      sections.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_2,
          children: [new TextRun({ text: roleName })]
        })
      );
      
      const statusColor = getStatusColor(data.status);
      
      sections.push(
        new Table({
          columnWidths: [3000, 4000, 2360],
          rows: [
            // Header row
            new TableRow({
              tableHeader: true,
              children: ['Parameter', 'Value', 'Status'].map(text =>
                new TableCell({
                  borders: cellBorders,
                  shading: { fill: STATUS_COLORS.HEADER.hex, type: ShadingType.CLEAR },
                  children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 20 })] })]
                })
              )
            }),
            // Data rows
            new TableRow({
              children: [
                new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: 'Location', size: 20 })] })] }),
                new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: data.location || 'N/A', size: 20 })] })] }),
                new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: '-', size: 20 })] })] })
              ]
            }),
            new TableRow({
              children: [
                new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: 'Beds', size: 20 })] })] }),
                new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: `${data.beds_occupied || 0}/${data.beds_total || 0} (${formatNum(data.occupancy_rate, 0)}% occupied)`, size: 20 })] })] }),
                new TableCell({
                  borders: cellBorders,
                  shading: { fill: statusColor.bgHex, type: ShadingType.CLEAR },
                  children: [new Paragraph({ children: [new TextRun({ text: data.status || 'UNKNOWN', bold: true, color: statusColor.hex, size: 20 })] })]
                })
              ]
            }),
            new TableRow({
              children: [
                new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: 'Surgical Teams', size: 20 })] })] }),
                new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: String(data.surgical_teams || 0), size: 20 })] })] }),
                new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: data.surgical_status || 'N/A', size: 20 })] })] })
              ]
            })
          ]
        })
      );
      
      sections.push(new Paragraph({ spacing: { after: 200 }, children: [] }));
    }
  });
  
  // Section 2: MEDEVAC Assets
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: '2. MEDEVAC Assets' })]
    })
  );
  
  const ma = reportData.medevac_assets || {};
  const medevacStatus = getStatusColor(ma.status);
  
  sections.push(
    new Table({
      columnWidths: [3000, 4000, 2360],
      rows: [
        new TableRow({
          tableHeader: true,
          children: ['Parameter', 'Value', 'Status'].map(text =>
            new TableCell({
              borders: cellBorders,
              shading: { fill: STATUS_COLORS.HEADER.hex, type: ShadingType.CLEAR },
              children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 20 })] })]
            })
          )
        }),
        new TableRow({
          children: [
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: 'Availability', size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: `${ma.available || 0}/${ma.total || 0} (${formatNum(ma.availability_rate, 0)}%)`, size: 20 })] })] }),
            new TableCell({
              borders: cellBorders,
              shading: { fill: medevacStatus.bgHex, type: ShadingType.CLEAR },
              children: [new Paragraph({ children: [new TextRun({ text: ma.status || 'UNKNOWN', bold: true, color: medevacStatus.hex, size: 20 })] })]
            })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: 'Missions (24h)', size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: String(ma.missions_last_24h || 0), size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: ma.avg_response_time != null ? `Avg: ${formatNum(ma.avg_response_time, 0)} min` : 'N/A', size: 20 })] })] })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: 'Golden Hour', size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: fmtGoldenHour(ma.golden_hour_achievement), size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: goldenHourStatus(ma.golden_hour_achievement), size: 20 })] })] })
          ]
        })
      ]
    })
  );
  
  // Section 3: Battle Casualties
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400 },
      children: [new TextRun({ text: '3. Battle Casualties' })]
    })
  );
  
  const bc = reportData.battle_casualties || {};
  sections.push(
    new Table({
      columnWidths: [6000, 3360],
      rows: [
        new TableRow({
          tableHeader: true,
          children: ['Category', 'Count'].map(text =>
            new TableCell({
              borders: cellBorders,
              shading: { fill: STATUS_COLORS.HEADER.hex, type: ShadingType.CLEAR },
              children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 20 })] })]
            })
          )
        }),
        ...['Total Battle Casualties', 'Killed in Action (KIA)', 'Died of Wounds (DOW)', 'Wounded in Action (WIA)', 'Returned to Duty (RTD)'].map((label, i) => {
          const values = [bc.total || 0, bc.kia || 0, bc.dow || 0, bc.wia || 0, bc.rtn || 0];
          return new TableRow({
            children: [
              new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: label, bold: i === 0, size: 20 })] })] }),
              new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: String(values[i]), bold: i === 0, size: 20 })] })] })
            ]
          });
        })
      ]
    })
  );
  
  // Section 4: DNBI
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400 },
      children: [new TextRun({ text: '4. Disease and Non-Battle Injuries' })]
    })
  );
  
  const dnbi = reportData.dnbi || {};
  sections.push(
    new Table({
      columnWidths: [6000, 3360],
      rows: [
        new TableRow({
          tableHeader: true,
          children: ['Metric', 'Value'].map(text =>
            new TableCell({
              borders: cellBorders,
              shading: { fill: STATUS_COLORS.HEADER.hex, type: ShadingType.CLEAR },
              children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 20 })] })]
            })
          )
        }),
        new TableRow({
          children: [
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: 'Total DNBI', bold: true, size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: String(dnbi.total || 0), bold: true, size: 20 })] })] })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: 'Rate per 1000/day', size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: formatNum(dnbi.rate_per_1000, 2), size: 20 })] })] })
          ]
        })
      ]
    })
  );
  
  return sections;
}

/**
 * Generate Casualty Estimate content for DOCX
 * @private
 */
function generateCasualtyEstimateDOCX(reportData, cellBorders) {
  const sections = [];
  
  // Executive Summary
  const es = reportData.executive_summary || {};
  
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: 'Executive Summary' })]
    })
  );
  
  // BLUF
  sections.push(
    new Paragraph({
      spacing: { after: 200 },
      shading: { fill: 'E3F2FD', type: ShadingType.CLEAR },
      children: [
        new TextRun({ text: 'BLUF: ', bold: true, size: 22 }),
        new TextRun({ text: es.bottom_line_up_front || 'N/A', size: 22 })
      ]
    })
  );
  
  // Key metrics table
  const statusColor = getStatusColor(es.medical_capacity_status);
  sections.push(
    new Table({
      columnWidths: [4680, 4680],
      rows: [
        new TableRow({
          children: [
            new TableCell({
              borders: cellBorders,
              shading: { fill: 'F5F5F5', type: ShadingType.CLEAR },
              children: [
                new Paragraph({ children: [new TextRun({ text: 'Total Casualties', size: 18, color: '666666' })] }),
                new Paragraph({ children: [new TextRun({ text: String(es.total_casualties || 0), bold: true, size: 36, color: '1976D2' })] })
              ]
            }),
            new TableCell({
              borders: cellBorders,
              shading: { fill: 'F5F5F5', type: ShadingType.CLEAR },
              children: [
                new Paragraph({ children: [new TextRun({ text: 'Battle Casualties', size: 18, color: '666666' })] }),
                new Paragraph({ children: [new TextRun({ text: String(es.battle_casualties || 0), bold: true, size: 36, color: '1976D2' })] })
              ]
            })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({
              borders: cellBorders,
              shading: { fill: 'F5F5F5', type: ShadingType.CLEAR },
              children: [
                new Paragraph({ children: [new TextRun({ text: 'DNBI', size: 18, color: '666666' })] }),
                new Paragraph({ children: [new TextRun({ text: String(es.dnbi || 0), bold: true, size: 36, color: '1976D2' })] })
              ]
            }),
            new TableCell({
              borders: cellBorders,
              shading: { fill: statusColor.bgHex, type: ShadingType.CLEAR },
              children: [
                new Paragraph({ children: [new TextRun({ text: 'Medical Capacity', size: 18, color: '666666' })] }),
                new Paragraph({ children: [new TextRun({ text: es.medical_capacity_status || 'UNKNOWN', bold: true, size: 36, color: statusColor.hex })] })
              ]
            })
          ]
        })
      ]
    })
  );
  
  // Battle Casualties Analysis
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400 },
      children: [new TextRun({ text: 'Battle Casualties Analysis' })]
    })
  );
  
  const bc = reportData.battle_casualties || {};
  sections.push(
    new Table({
      columnWidths: [4000, 2680, 2680],
      rows: [
        new TableRow({
          tableHeader: true,
          children: ['Category', 'Count', 'Rate'].map(text =>
            new TableCell({
              borders: cellBorders,
              shading: { fill: STATUS_COLORS.HEADER.hex, type: ShadingType.CLEAR },
              children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 20 })] })]
            })
          )
        }),
        new TableRow({
          children: [
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: 'Total BC', bold: true, size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: String(bc.total || 0), bold: true, size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: `${formatNum(bc.casualty_rate, 2)}/1000/day`, size: 20 })] })] })
          ]
        }),
        ...['KIA', 'DOW', 'WIA'].map((label, i) => {
          const values = [bc.kia || 0, bc.dow || 0, bc.wia || 0];
          return new TableRow({
            children: [
              new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: label, size: 20 })] })] }),
              new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: String(values[i]), size: 20 })] })] }),
              new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: '-', size: 20 })] })] })
            ]
          });
        })
      ]
    })
  );
  
  // MTF Capacity Analysis
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400 },
      children: [new TextRun({ text: 'MTF Capacity Analysis' })]
    })
  );
  
  const mtf = reportData.mtf_capacity || {};
  const mtfRows = [];
  
  // Header
  mtfRows.push(
    new TableRow({
      tableHeader: true,
      children: ['Role', 'Beds', 'Occupancy', 'Status'].map(text =>
        new TableCell({
          borders: cellBorders,
          shading: { fill: STATUS_COLORS.HEADER.hex, type: ShadingType.CLEAR },
          children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 20 })] })]
        })
      )
    })
  );
  
  // Data rows
  ['role2_basic', 'role2_enhanced', 'role3'].forEach(role => {
    const data = mtf[role];
    if (data) {
      const statusCol = getStatusColor(data.status);
      mtfRows.push(
        new TableRow({
          children: [
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: data.role || role, size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: `${data.beds_occupied || 0}/${data.beds_total || 0}`, size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: `${formatNum(data.occupancy_rate, 0)}%`, size: 20 })] })] }),
            new TableCell({
              borders: cellBorders,
              shading: { fill: statusCol.bgHex, type: ShadingType.CLEAR },
              children: [new Paragraph({ children: [new TextRun({ text: data.status || 'UNKNOWN', bold: true, color: statusCol.hex, size: 20 })] })]
            })
          ]
        })
      );
    }
  });
  
  sections.push(new Table({ columnWidths: [2340, 2340, 2340, 2340], rows: mtfRows }));
  
  return sections;
}

/**
 * Generate Components Matrix content for DOCX
 * @private
 */
function generateComponentsMatrixDOCX(reportData, cellBorders) {
  const sections = [];
  
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: 'Components of Care Status Matrix' })]
    })
  );
  
  const components = reportData.components || {};
  const componentNames = {
    personnel: 'Medical Personnel',
    facilities: 'Medical Treatment Facilities',
    medevac: 'Medical Evacuation',
    medlog: 'Medical Logistics',
    hns: 'Host Nation Support',
    c2: 'Command & Control'
  };
  
  const rows = [];
  
  // Header
  rows.push(
    new TableRow({
      tableHeader: true,
      children: ['Component', 'Status', 'Percentage', 'Assessment'].map(text =>
        new TableCell({
          borders: cellBorders,
          shading: { fill: STATUS_COLORS.HEADER.hex, type: ShadingType.CLEAR },
          children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 20 })] })]
        })
      )
    })
  );
  
  // Component rows
  Object.entries(componentNames).forEach(([key, name]) => {
    const data = components[key];
    if (data) {
      const statusCol = getStatusColor(data.status);
      rows.push(
        new TableRow({
          children: [
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: name, bold: true, size: 20 })] })] }),
            new TableCell({
              borders: cellBorders,
              shading: { fill: statusCol.bgHex, type: ShadingType.CLEAR },
              children: [new Paragraph({ children: [new TextRun({ text: data.status || 'UNKNOWN', bold: true, color: statusCol.hex, size: 20 })] })]
            }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: `${formatNum(data.percentage, 0)}%`, size: 20 })] })] }),
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: data.assessment || '-', size: 20 })] })] })
          ]
        })
      );
    }
  });
  
  // Overall row
  const overall = components.overall;
  if (overall) {
    const overallColor = getStatusColor(overall.status);
    rows.push(
      new TableRow({
        children: [
          new TableCell({
            borders: cellBorders,
            shading: { fill: 'E0E0E0', type: ShadingType.CLEAR },
            children: [new Paragraph({ children: [new TextRun({ text: 'OVERALL MEDICAL POSTURE', bold: true, size: 20 })] })]
          }),
          new TableCell({
            borders: cellBorders,
            shading: { fill: overallColor.bgHex, type: ShadingType.CLEAR },
            children: [new Paragraph({ children: [new TextRun({ text: overall.status || 'UNKNOWN', bold: true, color: overallColor.hex, size: 22 })] })]
          }),
          new TableCell({
            borders: cellBorders,
            shading: { fill: 'E0E0E0', type: ShadingType.CLEAR },
            children: [new Paragraph({ children: [new TextRun({ text: '-', size: 20 })] })]
          }),
          new TableCell({
            borders: cellBorders,
            shading: { fill: 'E0E0E0', type: ShadingType.CLEAR },
            children: [new Paragraph({ children: [new TextRun({ text: overall.assessment || '-', bold: true, size: 20 })] })]
          })
        ]
      })
    );
  }
  
  sections.push(new Table({ columnWidths: [2800, 1800, 1800, 2960], rows }));
  
  return sections;
}

/**
 * Generate 9-LINE MEDEVAC content for DOCX
 * @private
 */
function generateNineLineDOCX(reportData, cellBorders) {
  const sections = [];
  
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: '9-LINE MEDEVAC Request' })]
    })
  );
  
  const lines = [
    { num: 1, desc: 'Location of Pickup Site', key: 'line_1' },
    { num: 2, desc: 'Radio Frequency', key: 'line_2' },
    { num: 3, desc: 'Number of Patients by Precedence', key: 'line_3' },
    { num: 4, desc: 'Special Equipment Required', key: 'line_4' },
    { num: 5, desc: 'Number of Patients by Type', key: 'line_5' },
    { num: 6, desc: 'Security at Pickup Site', key: 'line_6' },
    { num: 7, desc: 'Method of Marking Pickup Site', key: 'line_7' },
    { num: 8, desc: 'Patient Nationality and Status', key: 'line_8' },
    { num: 9, desc: 'NBC Contamination', key: 'line_9' }
  ];
  
  const rows = [
    new TableRow({
      tableHeader: true,
      children: ['Line', 'Description', 'Value'].map(text =>
        new TableCell({
          borders: cellBorders,
          shading: { fill: STATUS_COLORS.HEADER.hex, type: ShadingType.CLEAR },
          children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 20 })] })]
        })
      )
    })
  ];
  
  lines.forEach(line => {
    const value = reportData[line.key] || reportData.lines?.[line.key] || 'N/A';
    rows.push(
      new TableRow({
        children: [
          new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: `Line ${line.num}`, bold: true, size: 20 })] })] }),
          new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: line.desc, size: 20 })] })] }),
          new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: String(value), size: 20 })] })] })
        ]
      })
    );
  });
  
  sections.push(new Table({ columnWidths: [1500, 4430, 3430], rows }));
  
  return sections;
}

/**
 * Generate METHANE content for DOCX
 * @private
 */
function generateMETHANEDOCX(reportData, cellBorders) {
  const sections = [];
  
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: 'METHANE Report - Major Incident' })]
    })
  );
  
  const elements = [
    { code: 'M', desc: 'Major Incident Declaration', key: 'major_incident' },
    { code: 'E', desc: 'Exact Location', key: 'exact_location' },
    { code: 'T', desc: 'Type of Incident', key: 'type_of_incident' },
    { code: 'H', desc: 'Hazards Present', key: 'hazards' },
    { code: 'A', desc: 'Access and Egress Routes', key: 'access' },
    { code: 'N', desc: 'Number of Casualties', key: 'number_of_casualties' },
    { code: 'E', desc: 'Emergency Services Required', key: 'emergency_services' }
  ];
  
  const rows = [
    new TableRow({
      tableHeader: true,
      children: ['Element', 'Description', 'Details'].map(text =>
        new TableCell({
          borders: cellBorders,
          shading: { fill: STATUS_COLORS.HEADER.hex, type: ShadingType.CLEAR },
          children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 20 })] })]
        })
      )
    })
  ];
  
  elements.forEach(el => {
    const value = reportData[el.key] || reportData.methane?.[el.key] || 'N/A';
    rows.push(
      new TableRow({
        children: [
          new TableCell({
            borders: cellBorders,
            shading: { fill: 'F5F5F5', type: ShadingType.CLEAR },
            children: [new Paragraph({ children: [new TextRun({ text: el.code, bold: true, size: 24, color: 'C62828' })] })]
          }),
          new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: el.desc, size: 20 })] })] }),
          new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: String(value), size: 20 })] })] })
        ]
      })
    );
  });
  
  sections.push(new Table({ columnWidths: [1200, 3680, 4480], rows }));
  
  return sections;
}

/**
 * Generate Executive Brief content for DOCX
 * @private
 */
function generateExecutiveBriefDOCX(reportData, cellBorders) {
  const sections = [];
  
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: 'Executive Brief' })]
    })
  );
  
  const brief = reportData.executive_brief || reportData;
  const bluf = brief.bluf || brief.executive_summary?.bottom_line_up_front || 'N/A';
  
  sections.push(
    new Paragraph({
      shading: { fill: 'E3F2FD', type: ShadingType.CLEAR },
      spacing: { after: 300 },
      children: [
        new TextRun({ text: 'BLUF: ', bold: true, size: 24 }),
        new TextRun({ text: bluf, size: 22 })
      ]
    })
  );
  
  // Key metrics
  const metrics = brief.key_metrics || [];
  if (metrics.length > 0) {
    const rows = [];
    metrics.forEach(m => {
      const statusCol = getStatusColor(m.status);
      rows.push(
        new TableRow({
          children: [
            new TableCell({ borders: cellBorders, children: [new Paragraph({ children: [new TextRun({ text: m.label || 'Metric', size: 20 })] })] }),
            new TableCell({
              borders: cellBorders,
              shading: m.status ? { fill: statusCol.bgHex, type: ShadingType.CLEAR } : {},
              children: [new Paragraph({ children: [new TextRun({ text: String(m.value || 0), bold: true, size: 24, color: m.status ? statusCol.hex : '000000' })] })]
            })
          ]
        })
      );
    });
    
    sections.push(new Table({ columnWidths: [4680, 4680], rows }));
  }
  
  // Recommendations
  const recommendations = brief.recommendations || [];
  if (recommendations.length > 0) {
    sections.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 300 },
        children: [new TextRun({ text: 'Recommendations' })]
      })
    );
    
    recommendations.forEach((rec, i) => {
      sections.push(
        new Paragraph({
          spacing: { after: 100 },
          children: [new TextRun({ text: `${i + 1}. ${rec}`, size: 20 })]
        })
      );
    });
  }
  
  return sections;
}

/**
 * Generate MEDASSESSREP content for DOCX
 * @private
 */
function generateMEDASSESSREPDOCX(reportData, cellBorders) {
  const sections = [];
  const evac = reportData.evacuation || {};
  const bc = reportData.battle_casualties || {};
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: 'MEDASSESSREP - Medical Assessment Report', bold: true, size: 28 })],
    spacing: { after: 200 }
  }));
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: `Assessment Period: ${reportData.metadata?.reporting_period || 'Weekly'}` })],
    spacing: { after: 200 }
  }));
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: '1. Medical Situation Assessment', bold: true, size: 24 })],
    spacing: { before: 200, after: 100 }
  }));
  
  sections.push(new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: cellBorders,
    rows: [
      new TableRow({
        children: ['Metric', 'Current', 'Status'].map(text => 
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text, bold: true })] })], shading: { fill: '1976D2' } })
        )
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph('Battle Casualties')] }),
          new TableCell({ children: [new Paragraph(String(bc.total || 0))] }),
          new TableCell({ children: [new Paragraph(bc.status || 'GREEN')], shading: { fill: bc.status === 'RED' ? 'FFCDD2' : bc.status === 'AMBER' ? 'FFE0B2' : 'C8E6C9' } })
        ]
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph('Golden Hour %')] }),
          new TableCell({ children: [new Paragraph(fmtGoldenHour(evac.golden_hour_achievement))] }),
          new TableCell({ children: [new Paragraph(evac.golden_hour_achievement == null ? 'PENDING' : evac.golden_hour_achievement >= 80 ? 'GREEN' : evac.golden_hour_achievement >= 60 ? 'AMBER' : 'RED')], shading: { fill: evac.golden_hour_achievement == null ? 'F5F5F5' : evac.golden_hour_achievement >= 80 ? 'C8E6C9' : evac.golden_hour_achievement >= 60 ? 'FFE0B2' : 'FFCDD2' } })
        ]
      })
    ]
  }));
  
  return sections;
}

/**
 * Generate EPINATO2 content for DOCX
 * @private
 */
function generateEPINATO2DOCX(reportData, cellBorders) {
  const sections = [];
  const dnbi = reportData.dnbi || {};
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: 'EPINATO2 - Disease/Injury Surveillance Report', bold: true, size: 28 })],
    spacing: { after: 100 }
  }));
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: 'IAW STANAG 2535 - Medical Surveillance', italics: true })],
    spacing: { after: 200 }
  }));
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: 'Section A: DNBI Summary', bold: true, size: 24 })],
    spacing: { before: 200, after: 100 }
  }));
  
  sections.push(new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: cellBorders,
    rows: [
      new TableRow({
        children: ['Category', 'Cases', 'Rate/1000', 'Status'].map(text => 
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text, bold: true })] })], shading: { fill: '1976D2' } })
        )
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph('Total DNBI')] }),
          new TableCell({ children: [new Paragraph(String(dnbi.total || 0))] }),
          new TableCell({ children: [new Paragraph(String(dnbi.rate_per_1000 || 0))] }),
          new TableCell({ children: [new Paragraph(dnbi.status || 'GREEN')], shading: { fill: dnbi.status === 'RED' ? 'FFCDD2' : dnbi.status === 'AMBER' ? 'FFE0B2' : 'C8E6C9' } })
        ]
      })
    ]
  }));
  
  return sections;
}

/**
 * Generate OOSR content for DOCX
 * @private
 */
function generateOOSRDOCX(reportData, cellBorders) {
  const sections = [];
  const items = reportData.out_of_standards?.items || [];
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: 'OUT OF STANDARDS REPORT (OOSR)', bold: true, size: 28 })],
    spacing: { after: 200 }
  }));
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: `Total Items Out of Standard: ${items.length}`, bold: true })],
    spacing: { after: 200 }
  }));
  
  if (items.length > 0) {
    const rows = [
      new TableRow({
        children: ['Item', 'Standard', 'Actual', 'Severity', 'Action'].map(text => 
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text, bold: true })] })], shading: { fill: '1976D2' } })
        )
      })
    ];
    items.forEach(item => {
      rows.push(new TableRow({
        children: [
          new TableCell({ children: [new Paragraph(item.item || '')] }),
          new TableCell({ children: [new Paragraph(item.standard || '')] }),
          new TableCell({ children: [new Paragraph(item.actual || '')] }),
          new TableCell({ children: [new Paragraph(item.severity || 'MINOR')], shading: { fill: item.severity === 'CRITICAL' ? 'FFCDD2' : item.severity === 'MAJOR' ? 'FFE0B2' : 'C8E6C9' } }),
          new TableCell({ children: [new Paragraph(item.action || '')] })
        ]
      }));
    });
    sections.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: cellBorders, rows }));
  } else {
    sections.push(new Paragraph({ children: [new TextRun({ text: '✓ All items within NATO standards', color: '2E7D32', bold: true })] }));
  }
  
  return sections;
}

/**
 * Generate Blue Light content for DOCX
 * @private
 */
function generateBlueLightDOCX(reportData, cellBorders) {
  const sections = [];
  const blueLight = reportData.blue_light || {};
  const capacity = blueLight.capacity || {};
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: 'BLUE LIGHT PROTOCOL REPORT', bold: true, size: 28 })],
    spacing: { after: 200 }
  }));
  
  sections.push(new Paragraph({
    children: [
      new TextRun({ text: 'Blue Light Status: ', bold: true }),
      new TextRun({ text: blueLight.activated ? 'ACTIVATED' : 'NOT ACTIVATED', bold: true, color: blueLight.activated ? 'C62828' : '2E7D32' })
    ],
    spacing: { after: 200 }
  }));
  
  sections.push(new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: cellBorders,
    rows: [
      new TableRow({
        children: ['Resource', 'Required', 'Available', 'Gap', 'Status'].map(text => 
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text, bold: true })] })], shading: { fill: '1976D2' } })
        )
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph('Surgical Teams')] }),
          new TableCell({ children: [new Paragraph(String(capacity.surgical_required || 0))] }),
          new TableCell({ children: [new Paragraph(String(capacity.surgical_available || 0))] }),
          new TableCell({ children: [new Paragraph(String((capacity.surgical_required || 0) - (capacity.surgical_available || 0)))] }),
          new TableCell({ children: [new Paragraph(capacity.surgical_status || 'GREEN')], shading: { fill: capacity.surgical_status === 'RED' ? 'FFCDD2' : capacity.surgical_status === 'AMBER' ? 'FFE0B2' : 'C8E6C9' } })
        ]
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph('Blood Units')] }),
          new TableCell({ children: [new Paragraph(String(capacity.blood_required || 0))] }),
          new TableCell({ children: [new Paragraph(String(capacity.blood_available || 0))] }),
          new TableCell({ children: [new Paragraph(String((capacity.blood_required || 0) - (capacity.blood_available || 0)))] }),
          new TableCell({ children: [new Paragraph(capacity.blood_status || 'GREEN')], shading: { fill: capacity.blood_status === 'RED' ? 'FFCDD2' : capacity.blood_status === 'AMBER' ? 'FFE0B2' : 'C8E6C9' } })
        ]
      })
    ]
  }));
  
  return sections;
}

/**
 * Generate MEDEVAC Summary content for DOCX
 * @private
 */
function generateMEDEVACSummaryDOCX(reportData, cellBorders) {
  const sections = [];
  const medevac = reportData.medevac_assets || {};
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: 'MEDEVAC SUMMARY REPORT', bold: true, size: 28 })],
    spacing: { after: 200 }
  }));
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: 'Asset Availability', bold: true, size: 24 })],
    spacing: { before: 200, after: 100 }
  }));
  
  sections.push(new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: cellBorders,
    rows: [
      new TableRow({
        children: ['Asset', 'Total', 'Available', 'On Mission', 'Status'].map(text => 
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text, bold: true })] })], shading: { fill: '1976D2' } })
        )
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph('Helicopters')] }),
          new TableCell({ children: [new Paragraph(String(medevac.total || 0))] }),
          new TableCell({ children: [new Paragraph(String(medevac.available || 0))] }),
          new TableCell({ children: [new Paragraph(String(medevac.missions_last_24h || 0))] }),
          new TableCell({ children: [new Paragraph(medevac.status || 'GREEN')], shading: { fill: medevac.status === 'RED' ? 'FFCDD2' : medevac.status === 'AMBER' ? 'FFE0B2' : 'C8E6C9' } })
        ]
      })
    ]
  }));
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: `Golden Hour Achievement: ${medevac.golden_hour_achievement || 0}%` })],
    spacing: { before: 200 }
  }));
  
  return sections;
}

/**
 * Generate NATO Planning Format content for DOCX
 * @private
 */
function generateNATOPlanningDOCX(reportData, cellBorders) {
  const sections = [];
  const exec = reportData.executive_summary || {};
  const bc = reportData.battle_casualties || {};
  const components = reportData.components || {};
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: 'NATO MEDICAL PLANNING FORMAT', bold: true, size: 28 })],
    spacing: { after: 100 }
  }));
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: 'IAW AJMedP-5 Allied Joint Medical Planning Doctrine', italics: true })],
    spacing: { after: 200 }
  }));
  
  sections.push(new Paragraph({
    children: [new TextRun({ text: 'ANNEX Q - MEDICAL ESTIMATE', bold: true, size: 24 })],
    spacing: { before: 200, after: 200 }
  }));
  
  // Casualty Estimate Table
  sections.push(new Paragraph({ children: [new TextRun({ text: 'Part 2: Casualty Estimate', bold: true })], spacing: { after: 100 } }));
  
  sections.push(new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: cellBorders,
    rows: [
      new TableRow({
        children: ['Category', 'Estimate', 'Daily Rate'].map(text => 
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text, bold: true })] })], shading: { fill: '1976D2' } })
        )
      }),
      new TableRow({ children: [new TableCell({ children: [new Paragraph('Battle Casualties')] }), new TableCell({ children: [new Paragraph(String(bc.total || 0))] }), new TableCell({ children: [new Paragraph(String(bc.daily_rate || 0))] })] }),
      new TableRow({ children: [new TableCell({ children: [new Paragraph('KIA')] }), new TableCell({ children: [new Paragraph(String(bc.kia || 0))] }), new TableCell({ children: [new Paragraph('-')] })] }),
      new TableRow({ children: [new TableCell({ children: [new Paragraph('WIA')] }), new TableCell({ children: [new Paragraph(String(bc.wia || 0))] }), new TableCell({ children: [new Paragraph('-')] })] }),
      new TableRow({ children: [new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'TOTAL', bold: true })] })] }), new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(exec.total_casualties || 0), bold: true })] })] }), new TableCell({ children: [new Paragraph('-')] })] })
    ]
  }));
  
  // Components Table
  sections.push(new Paragraph({ children: [new TextRun({ text: 'Part 3: Medical Support Concept', bold: true })], spacing: { before: 200, after: 100 } }));
  
  sections.push(new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: cellBorders,
    rows: [
      new TableRow({
        children: ['Component', 'Status', 'Assessment'].map(text => 
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text, bold: true })] })], shading: { fill: '1976D2' } })
        )
      }),
      ...['facilities', 'medevac', 'medlog', 'personnel'].map(key => 
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph(key.charAt(0).toUpperCase() + key.slice(1))] }),
            new TableCell({ children: [new Paragraph(components[key]?.status || 'GREEN')], shading: { fill: components[key]?.status === 'RED' ? 'FFCDD2' : components[key]?.status === 'AMBER' ? 'FFE0B2' : 'C8E6C9' } }),
            new TableCell({ children: [new Paragraph(components[key]?.assessment || 'Adequate')] })
          ]
        })
      )
    ]
  }));
  
  // Conclusion
  sections.push(new Paragraph({
    children: [
      new TextRun({ text: 'Overall Supportability: ', bold: true }),
      new TextRun({ text: components.overall?.status || 'GREEN', bold: true, color: components.overall?.status === 'RED' ? 'C62828' : components.overall?.status === 'AMBER' ? 'F57C00' : '2E7D32' })
    ],
    spacing: { before: 200 }
  }));
  
  return sections;
}

/**
 * Generate generic content for DOCX
 * @private
 */
function generateGenericDOCX(reportData, cellBorders) {
  const sections = [];
  
  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: 'Report Data' })]
    })
  );
  
  sections.push(
    new Paragraph({
      children: [new TextRun({ text: JSON.stringify(reportData, null, 2), size: 18, font: 'Courier New' })]
    })
  );
  
  return sections;
}

// ============================================================================
// PDF EXPORT - FULL IMPLEMENTATION
// ============================================================================

/**
 * Export report as PDF
 * 
 * @param {Object} reportData - Formatted report data
 * @param {Object} options - Export options
 * @returns {Promise<Blob>} PDF Blob ready for download
 */
export async function exportToPDF(reportData, options = {}) {
  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let currentY = margin;
    
    // Helper function to add new page if needed
    const checkPageBreak = (requiredSpace) => {
      if (currentY + requiredSpace > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
        return true;
      }
      return false;
    };
    
    // Helper function to add header on each page
    const addHeader = () => {
      doc.setFontSize(8);
      doc.setTextColor(102, 102, 102);
      doc.text(reportData.metadata?.classification || 'NATO UNCLASSIFIED', pageWidth - margin, 10, { align: 'right' });
    };
    
    addHeader();
    
    // Title
    doc.setFontSize(24);
    doc.setTextColor(25, 118, 210);
    doc.setFont(undefined, 'bold');
    doc.text(reportData.metadata?.report_type || 'Medical Report', pageWidth / 2, currentY, { align: 'center' });
    currentY += 10;
    
    // Subtitle
    doc.setFontSize(12);
    doc.setTextColor(102, 102, 102);
    doc.setFont(undefined, 'italic');
    doc.text(reportData.metadata?.full_name || '', pageWidth / 2, currentY, { align: 'center' });
    currentY += 15;
    
    // Metadata table
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    
    const metaData = [
      ['Reporting Unit', reportData.metadata?.reporting_unit || 'N/A'],
      ['Date/Time Group', reportData.metadata?.dtg || new Date().toISOString()],
      ['Reporting Period', reportData.metadata?.reporting_period || 'N/A'],
      ['Classification', reportData.metadata?.classification || 'NATO UNCLASSIFIED']
    ];
    
    autoTable(doc, {
      startY: currentY,
      head: [],
      body: metaData,
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 2 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        1: { cellWidth: contentWidth - 40 }
      },
      margin: { left: margin, right: margin }
    });
    
    currentY = doc.lastAutoTable.finalY + 15;
    
    // Report-specific content
    const reportType = reportData.metadata?.report_type;
    
    if (reportType === 'MEDSITREP') {
      generateMEDSITREPPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    } else if (reportType === 'CASUALTY_ESTIMATE') {
      generateCasualtyEstimatePDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    } else if (reportType === 'COMPONENTS_MATRIX') {
      generateComponentsMatrixPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    } else if (reportType === 'NINE_LINE' || reportType === '9-LINE MEDEVAC') {
      generateNineLinePDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    } else if (reportType === 'METHANE') {
      generateMETHANEPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    } else if (reportType === 'MEDASSESSREP') {
      generateMEDASSESSREPPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    } else if (reportType === 'EPINATO2') {
      generateEPINATO2PDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    } else if (reportType === 'OOSR') {
      generateOOSRPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    } else if (reportType === 'BLUE_LIGHT') {
      generateBlueLightPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    } else if (reportType === 'MEDEVAC_SUMMARY') {
      generateMEDEVACSummaryPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    } else if (reportType === 'NATO_PLANNING') {
      generateNATOPlanningPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    } else {
      generateGenericPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
    }
    
    // Add footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(102, 102, 102);
      doc.text(`PSP/CRE Tool v3.0 | Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      doc.text(`Generated: ${new Date().toISOString()}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
    }
    
    const blob = doc.output('blob');
    // CL-001: dev-only log removed
    return blob;
    
  } catch (error) {
    console.error('❌ PDF export failed:', error);
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
}

/**
 * Generate MEDSITREP content for PDF
 * @private
 */
function generateMEDSITREPPDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  
  // Section 1: Deployed Hospital Care
  doc.setFontSize(14);
  doc.setTextColor(51, 51, 51);
  doc.setFont(undefined, 'bold');
  doc.text('1. Deployed Hospital Care', margin, currentY);
  currentY += 8;
  
  const dhc = reportData.deployed_hospital_care || {};
  
  ['role2_basic', 'role2_enhanced', 'role3'].forEach(role => {
    const data = dhc[role];
    if (data) {
      checkPageBreak(40);
      
      const roleName = role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(roleName, margin + 5, currentY);
      currentY += 5;
      
      autoTable(doc, {
        startY: currentY,
        head: [['Parameter', 'Value', 'Status']],
        body: [
          ['Location', data.location || 'N/A', '-'],
          ['Beds', `${data.beds_occupied || 0}/${data.beds_total || 0} (${formatNum(data.occupancy_rate, 0)}%)`, data.status || 'UNKNOWN'],
          ['Surgical Teams', String(data.surgical_teams || 0), data.surgical_status || 'N/A']
        ],
        theme: 'striped',
        headStyles: { fillColor: [25, 118, 210], textColor: 255 },
        styles: { fontSize: 9, cellPadding: 3 },
        margin: { left: margin, right: margin }
      });
      
      currentY = doc.lastAutoTable.finalY + 10;
    }
  });
  
  // Section 2: MEDEVAC Assets
  checkPageBreak(50);
  doc.setFontSize(14);
  doc.setTextColor(51, 51, 51);
  doc.setFont(undefined, 'bold');
  doc.text('2. MEDEVAC Assets', margin, currentY);
  currentY += 8;
  
  const ma = reportData.medevac_assets || {};
  
  autoTable(doc, {
    startY: currentY,
    head: [['Parameter', 'Value', 'Status']],
    body: [
      ['Availability', `${ma.available || 0}/${ma.total || 0} (${formatNum(ma.availability_rate, 0)}%)`, ma.status || 'UNKNOWN'],
      ['Missions (24h)', String(ma.missions_last_24h || 0), ma.avg_response_time != null ? `Avg: ${formatNum(ma.avg_response_time, 0)} min` : 'N/A'],
      ['Golden Hour', `${formatNum(ma.golden_hour_achievement, 0)}%`, (ma.golden_hour_achievement || 0) >= 90 ? 'Excellent' : (ma.golden_hour_achievement || 0) >= 70 ? 'Adequate' : 'Needs Improvement']
    ],
    theme: 'striped',
    headStyles: { fillColor: [25, 118, 210], textColor: 255 },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: margin, right: margin }
  });
  
  currentY = doc.lastAutoTable.finalY + 15;
  
  // Section 3: Battle Casualties
  checkPageBreak(50);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('3. Battle Casualties', margin, currentY);
  currentY += 8;
  
  const bc = reportData.battle_casualties || {};
  
  autoTable(doc, {
    startY: currentY,
    head: [['Category', 'Count']],
    body: [
      ['Total Battle Casualties', String(bc.total || 0)],
      ['Killed in Action (KIA)', String(bc.kia || 0)],
      ['Died of Wounds (DOW)', String(bc.dow || 0)],
      ['Wounded in Action (WIA)', String(bc.wia || 0)],
      ['Returned to Duty (RTD)', String(bc.rtn || 0)]
    ],
    theme: 'striped',
    headStyles: { fillColor: [25, 118, 210], textColor: 255 },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: margin, right: margin }
  });
  
  currentY = doc.lastAutoTable.finalY + 15;
  
  // Section 4: DNBI
  checkPageBreak(40);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('4. Disease and Non-Battle Injuries', margin, currentY);
  currentY += 8;
  
  const dnbi = reportData.dnbi || {};
  
  autoTable(doc, {
    startY: currentY,
    head: [['Metric', 'Value']],
    body: [
      ['Total DNBI', String(dnbi.total || 0)],
      ['Rate per 1000/day', formatNum(dnbi.rate_per_1000, 2)]
    ],
    theme: 'striped',
    headStyles: { fillColor: [25, 118, 210], textColor: 255 },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: margin, right: margin }
  });
}

/**
 * Generate Casualty Estimate content for PDF
 * @private
 */
function generateCasualtyEstimatePDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  
  const es = reportData.executive_summary || {};
  
  // Executive Summary
  doc.setFontSize(14);
  doc.setTextColor(51, 51, 51);
  doc.setFont(undefined, 'bold');
  doc.text('Executive Summary', margin, currentY);
  currentY += 10;
  
  // BLUF Box
  doc.setFillColor(227, 242, 253);
  doc.rect(margin, currentY - 5, contentWidth, 15, 'F');
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('BLUF: ', margin + 2, currentY + 3);
  doc.setFont(undefined, 'normal');
  const blufText = es.bottom_line_up_front || 'N/A';
  const blufLines = doc.splitTextToSize(blufText, contentWidth - 20);
  doc.text(blufLines, margin + 15, currentY + 3);
  currentY += 20;
  
  // Key metrics
  const statusColor = getStatusColor(es.medical_capacity_status);
  
  autoTable(doc, {
    startY: currentY,
    head: [],
    body: [
      ['Total Casualties', String(es.total_casualties || 0), 'Battle Casualties', String(es.battle_casualties || 0)],
      ['DNBI', String(es.dnbi || 0), 'Medical Capacity', es.medical_capacity_status || 'UNKNOWN']
    ],
    theme: 'grid',
    styles: { fontSize: 11, cellPadding: 5, halign: 'center' },
    columnStyles: {
      0: { fontStyle: 'normal', fillColor: [245, 245, 245] },
      1: { fontStyle: 'bold', textColor: [25, 118, 210] },
      2: { fontStyle: 'normal', fillColor: [245, 245, 245] },
      3: { fontStyle: 'bold', textColor: statusColor.rgb }
    },
    margin: { left: margin, right: margin }
  });
  
  currentY = doc.lastAutoTable.finalY + 15;
  
  // Battle Casualties Analysis
  checkPageBreak(60);
  doc.setFontSize(14);
  doc.setTextColor(51, 51, 51);
  doc.setFont(undefined, 'bold');
  doc.text('Battle Casualties Analysis', margin, currentY);
  currentY += 8;
  
  const bc = reportData.battle_casualties || {};
  
  autoTable(doc, {
    startY: currentY,
    head: [['Category', 'Count', 'Rate']],
    body: [
      ['Total BC', String(bc.total || 0), `${formatNum(bc.casualty_rate, 2)}/1000/day`],
      ['KIA', String(bc.kia || 0), '-'],
      ['DOW', String(bc.dow || 0), '-'],
      ['WIA', String(bc.wia || 0), '-']
    ],
    theme: 'striped',
    headStyles: { fillColor: [25, 118, 210], textColor: 255 },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: margin, right: margin }
  });
  
  currentY = doc.lastAutoTable.finalY + 15;
  
  // MTF Capacity
  checkPageBreak(60);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('MTF Capacity Analysis', margin, currentY);
  currentY += 8;
  
  const mtf = reportData.mtf_capacity || {};
  const mtfBody = [];
  
  ['role2_basic', 'role2_enhanced', 'role3'].forEach(role => {
    const data = mtf[role];
    if (data) {
      mtfBody.push([
        data.role || role,
        `${data.beds_occupied || 0}/${data.beds_total || 0}`,
        `${formatNum(data.occupancy_rate, 0)}%`,
        data.status || 'UNKNOWN'
      ]);
    }
  });
  
  autoTable(doc, {
    startY: currentY,
    head: [['Role', 'Beds', 'Occupancy', 'Status']],
    body: mtfBody,
    theme: 'striped',
    headStyles: { fillColor: [25, 118, 210], textColor: 255 },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: margin, right: margin }
  });
}

/**
 * Generate Components Matrix content for PDF
 * @private
 */
function generateComponentsMatrixPDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  
  doc.setFontSize(14);
  doc.setTextColor(51, 51, 51);
  doc.setFont(undefined, 'bold');
  doc.text('Components of Care Status Matrix', margin, currentY);
  currentY += 10;
  
  const components = reportData.components || {};
  const componentNames = {
    personnel: 'Medical Personnel',
    facilities: 'Medical Treatment Facilities',
    medevac: 'Medical Evacuation',
    medlog: 'Medical Logistics',
    hns: 'Host Nation Support',
    c2: 'Command & Control'
  };
  
  const body = [];
  Object.entries(componentNames).forEach(([key, name]) => {
    const data = components[key];
    if (data) {
      body.push([name, data.status || 'UNKNOWN', `${formatNum(data.percentage, 0)}%`, data.assessment || '-']);
    }
  });
  
  // Overall row
  if (components.overall) {
    body.push(['OVERALL MEDICAL POSTURE', components.overall.status || 'UNKNOWN', '-', components.overall.assessment || '-']);
  }
  
  autoTable(doc, {
    startY: currentY,
    head: [['Component', 'Status', '%', 'Assessment']],
    body: body,
    theme: 'striped',
    headStyles: { fillColor: [25, 118, 210], textColor: 255 },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: margin, right: margin }
  });
}

/**
 * Generate 9-LINE MEDEVAC content for PDF
 * @private
 */
function generateNineLinePDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  
  doc.setFontSize(14);
  doc.setTextColor(51, 51, 51);
  doc.setFont(undefined, 'bold');
  doc.text('9-LINE MEDEVAC Request', margin, currentY);
  currentY += 10;
  
  const lines = [
    { num: 1, desc: 'Location of Pickup Site', key: 'line_1' },
    { num: 2, desc: 'Radio Frequency', key: 'line_2' },
    { num: 3, desc: 'Number of Patients by Precedence', key: 'line_3' },
    { num: 4, desc: 'Special Equipment Required', key: 'line_4' },
    { num: 5, desc: 'Number of Patients by Type', key: 'line_5' },
    { num: 6, desc: 'Security at Pickup Site', key: 'line_6' },
    { num: 7, desc: 'Method of Marking Pickup Site', key: 'line_7' },
    { num: 8, desc: 'Patient Nationality and Status', key: 'line_8' },
    { num: 9, desc: 'NBC Contamination', key: 'line_9' }
  ];
  
  const body = lines.map(line => {
    const value = reportData[line.key] || reportData.lines?.[line.key] || 'N/A';
    return [`Line ${line.num}`, line.desc, String(value)];
  });
  
  autoTable(doc, {
    startY: currentY,
    head: [['Line', 'Description', 'Value']],
    body: body,
    theme: 'striped',
    headStyles: { fillColor: [25, 118, 210], textColor: 255 },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 20 } },
    margin: { left: margin, right: margin }
  });
}

/**
 * Generate METHANE content for PDF
 * @private
 */
function generateMETHANEPDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  
  doc.setFontSize(14);
  doc.setTextColor(51, 51, 51);
  doc.setFont(undefined, 'bold');
  doc.text('METHANE Report - Major Incident', margin, currentY);
  currentY += 10;
  
  const elements = [
    { code: 'M', desc: 'Major Incident Declaration', key: 'major_incident' },
    { code: 'E', desc: 'Exact Location', key: 'exact_location' },
    { code: 'T', desc: 'Type of Incident', key: 'type_of_incident' },
    { code: 'H', desc: 'Hazards Present', key: 'hazards' },
    { code: 'A', desc: 'Access and Egress Routes', key: 'access' },
    { code: 'N', desc: 'Number of Casualties', key: 'number_of_casualties' },
    { code: 'E', desc: 'Emergency Services Required', key: 'emergency_services' }
  ];
  
  const body = elements.map(el => {
    const value = reportData[el.key] || reportData.methane?.[el.key] || 'N/A';
    return [el.code, el.desc, String(value)];
  });
  
  autoTable(doc, {
    startY: currentY,
    head: [['Element', 'Description', 'Details']],
    body: body,
    theme: 'striped',
    headStyles: { fillColor: [25, 118, 210], textColor: 255 },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: { 0: { fontStyle: 'bold', textColor: [198, 40, 40], cellWidth: 15 } },
    margin: { left: margin, right: margin }
  });
}

/**
 * Generate MEDASSESSREP content for PDF
 * @private
 */
function generateMEDASSESSREPPDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  const bc = reportData.battle_casualties || {};
  const evac = reportData.evacuation || {};
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('MEDASSESSREP - Medical Assessment Report', margin, currentY);
  currentY += 10;
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Assessment Period: ${reportData.metadata?.reporting_period || 'Weekly'}`, margin, currentY);
  currentY += 10;
  
  autoTable(doc, {
    startY: currentY,
    head: [['Metric', 'Current', 'Status']],
    body: [
      ['Battle Casualties', String(bc.total || 0), bc.status || 'GREEN'],
      ['Golden Hour %', `${evac.golden_hour_achievement || 0}%`, evac.golden_hour_achievement >= 80 ? 'GREEN' : 'AMBER'],
      ['DOW Rate', `${evac.dow_rate || 0}%`, evac.dow_status || 'GREEN']
    ],
    margin: { left: margin },
    styles: { fontSize: 9 },
    headStyles: { fillColor: [25, 118, 210] }
  });
}

/**
 * Generate EPINATO2 content for PDF
 * @private
 */
function generateEPINATO2PDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  const dnbi = reportData.dnbi || {};
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('EPINATO2 - Disease/Injury Surveillance', margin, currentY);
  currentY += 8;
  
  doc.setFontSize(9);
  doc.setFont(undefined, 'italic');
  doc.text('IAW STANAG 2535', margin, currentY);
  currentY += 10;
  
  autoTable(doc, {
    startY: currentY,
    head: [['Category', 'Cases', 'Rate/1000', 'Status']],
    body: [['Total DNBI', String(dnbi.total || 0), String(dnbi.rate_per_1000 || 0), dnbi.status || 'GREEN']],
    margin: { left: margin },
    styles: { fontSize: 9 },
    headStyles: { fillColor: [25, 118, 210] }
  });
}

/**
 * Generate OOSR content for PDF
 * @private
 */
function generateOOSRPDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  const items = reportData.out_of_standards?.items || [];
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('OUT OF STANDARDS REPORT (OOSR)', margin, currentY);
  currentY += 10;
  
  doc.setFontSize(10);
  doc.text(`Total Items Out of Standard: ${items.length}`, margin, currentY);
  currentY += 10;
  
  if (items.length > 0) {
    autoTable(doc, {
      startY: currentY,
      head: [['Item', 'Standard', 'Actual', 'Severity']],
      body: items.map(item => [item.item, item.standard, item.actual, item.severity]),
      margin: { left: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [25, 118, 210] }
    });
  } else {
    doc.setTextColor(46, 125, 50);
    doc.text('All items within NATO standards', margin, currentY);
    doc.setTextColor(0);
  }
}

/**
 * Generate Blue Light content for PDF
 * @private
 */
function generateBlueLightPDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  const blueLight = reportData.blue_light || {};
  const capacity = blueLight.capacity || {};
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('BLUE LIGHT PROTOCOL REPORT', margin, currentY);
  currentY += 10;
  
  doc.setFontSize(10);
  doc.text(`Status: ${blueLight.activated ? 'ACTIVATED' : 'NOT ACTIVATED'}`, margin, currentY);
  currentY += 10;
  
  autoTable(doc, {
    startY: currentY,
    head: [['Resource', 'Required', 'Available', 'Gap', 'Status']],
    body: [
      ['Surgical Teams', capacity.surgical_required || 0, capacity.surgical_available || 0, (capacity.surgical_required || 0) - (capacity.surgical_available || 0), capacity.surgical_status || 'GREEN'],
      ['Blood Units', capacity.blood_required || 0, capacity.blood_available || 0, (capacity.blood_required || 0) - (capacity.blood_available || 0), capacity.blood_status || 'GREEN']
    ],
    margin: { left: margin },
    styles: { fontSize: 9 },
    headStyles: { fillColor: [25, 118, 210] }
  });
}

/**
 * Generate MEDEVAC Summary content for PDF
 * @private
 */
function generateMEDEVACSummaryPDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  const medevac = reportData.medevac_assets || {};
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('MEDEVAC SUMMARY REPORT', margin, currentY);
  currentY += 10;
  
  autoTable(doc, {
    startY: currentY,
    head: [['Asset', 'Total', 'Available', 'Status']],
    body: [['Helicopters', medevac.total || 0, medevac.available || 0, medevac.status || 'GREEN']],
    margin: { left: margin },
    styles: { fontSize: 9 },
    headStyles: { fillColor: [25, 118, 210] }
  });
  
  currentY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Golden Hour Achievement: ${medevac.golden_hour_achievement || 0}%`, margin, currentY);
}

/**
 * Generate NATO Planning Format content for PDF
 * @private
 */
function generateNATOPlanningPDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  const exec = reportData.executive_summary || {};
  const bc = reportData.battle_casualties || {};
  const components = reportData.components || {};
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('NATO MEDICAL PLANNING FORMAT', margin, currentY);
  currentY += 8;
  
  doc.setFontSize(9);
  doc.setFont(undefined, 'italic');
  doc.text('IAW AJMedP-5 Allied Joint Medical Planning Doctrine', margin, currentY);
  currentY += 10;
  
  doc.setFont(undefined, 'bold');
  doc.text('ANNEX Q - MEDICAL ESTIMATE', margin, currentY);
  currentY += 10;
  
  autoTable(doc, {
    startY: currentY,
    head: [['Category', 'Estimate', 'Daily Rate']],
    body: [
      ['Battle Casualties', bc.total || 0, bc.daily_rate || 0],
      ['KIA', bc.kia || 0, '-'],
      ['WIA', bc.wia || 0, '-'],
      ['TOTAL', exec.total_casualties || 0, '-']
    ],
    margin: { left: margin },
    styles: { fontSize: 9 },
    headStyles: { fillColor: [25, 118, 210] }
  });
  
  currentY = doc.lastAutoTable.finalY + 10;
  
  autoTable(doc, {
    startY: currentY,
    head: [['Component', 'Status', 'Assessment']],
    body: [
      ['Facilities', components.facilities?.status || 'GREEN', components.facilities?.assessment || 'Adequate'],
      ['MEDEVAC', components.medevac?.status || 'GREEN', components.medevac?.assessment || 'Adequate'],
      ['Medical Logistics', components.medlog?.status || 'GREEN', components.medlog?.assessment || 'Adequate'],
      ['Personnel', components.personnel?.status || 'GREEN', components.personnel?.assessment || 'Adequate']
    ],
    margin: { left: margin },
    styles: { fontSize: 9 },
    headStyles: { fillColor: [25, 118, 210] }
  });
}

/**
 * Generate generic content for PDF
 * @private
 */
function generateGenericPDF(doc, reportData, margin, contentWidth, startY, checkPageBreak) {
  let currentY = startY;
  
  doc.setFontSize(14);
  doc.setTextColor(51, 51, 51);
  doc.setFont(undefined, 'bold');
  doc.text('Report Data', margin, currentY);
  currentY += 10;
  
  doc.setFontSize(8);
  doc.setFont('Courier', 'normal');
  const jsonText = JSON.stringify(reportData, null, 2);
  const lines = jsonText.split('\n');
  
  lines.forEach(line => {
    if (currentY > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      currentY = 20;
    }
    doc.text(line, margin, currentY);
    currentY += 4;
  });
}

// ============================================================================
// XLSX EXPORT - FULL IMPLEMENTATION
// ============================================================================

/**
 * Export report as XLSX (Excel spreadsheet)
 * 
 * @param {Object} reportData - Formatted report data
 * @param {Object} options - Export options
 * @returns {Promise<Blob>} XLSX Blob ready for download
 */
export async function exportToXLSX(reportData, options = {}) {
  try {
    const workbook = XLSX.utils.book_new();
    
    // Sheet 1: Executive Summary
    const summaryData = generateSummarySheet(reportData);
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 30 }, { wch: 25 }, { wch: 20 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    
    // Report-specific sheets
    const reportType = reportData.metadata?.report_type;
    
    if (reportType === 'MEDSITREP') {
      generateMEDSITREPXLSX(workbook, reportData);
    } else if (reportType === 'CASUALTY_ESTIMATE') {
      generateCasualtyEstimateXLSX(workbook, reportData);
    } else if (reportType === 'COMPONENTS_MATRIX') {
      generateComponentsMatrixXLSX(workbook, reportData);
    } else if (reportType === 'NINE_LINE' || reportType === '9-LINE MEDEVAC') {
      generateNineLineXLSX(workbook, reportData);
    } else if (reportType === 'METHANE') {
      generateMETHANEXLSX(workbook, reportData);
    } else if (reportType === 'MEDASSESSREP') {
      generateMEDASSESSREPXLSX(workbook, reportData);
    } else if (reportType === 'EPINATO2') {
      generateEPINATO2XLSX(workbook, reportData);
    } else if (reportType === 'OOSR') {
      generateOOSRXLSX(workbook, reportData);
    } else if (reportType === 'BLUE_LIGHT') {
      generateBlueLightXLSX(workbook, reportData);
    } else if (reportType === 'MEDEVAC_SUMMARY') {
      generateMEDEVACSummaryXLSX(workbook, reportData);
    } else if (reportType === 'NATO_PLANNING') {
      generateNATOPlanningXLSX(workbook, reportData);
    }
    
    // Sheet: Raw Data (always include for analysis)
    const rawData = XLSX.utils.json_to_sheet([reportData], { header: Object.keys(reportData) });
    XLSX.utils.book_append_sheet(workbook, rawData, 'Raw Data');
    
    // Generate blob
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // CL-001: dev-only log removed
    return blob;
    
  } catch (error) {
    console.error('❌ XLSX export failed:', error);
    throw new Error(`Failed to generate XLSX: ${error.message}`);
  }
}

/**
 * Generate Summary sheet data
 * @private
 */
function generateSummarySheet(reportData) {
  const meta = reportData.metadata || {};
  const es = reportData.executive_summary || {};
  
  return [
    ['PSP/CRE Tool v3.0 - Medical Report'],
    [''],
    ['REPORT METADATA'],
    ['Report Type', meta.report_type || 'N/A'],
    ['Full Name', meta.full_name || 'N/A'],
    ['Reporting Unit', meta.reporting_unit || 'N/A'],
    ['Date/Time Group', meta.dtg || new Date().toISOString()],
    ['Reporting Period', meta.reporting_period || 'N/A'],
    ['Classification', meta.classification || 'NATO UNCLASSIFIED'],
    [''],
    ['EXECUTIVE SUMMARY'],
    ['Total Casualties', es.total_casualties || 0],
    ['Battle Casualties', es.battle_casualties || 0],
    ['DNBI', es.dnbi || 0],
    ['Medical Capacity Status', es.medical_capacity_status || 'UNKNOWN'],
    [''],
    ['Generated', new Date().toISOString()],
    ['Tool', 'PSP/CRE Tool v3.0'],
    ['Author', 'Dr. Tatar Radu MD, Romanian Military Medical Institute']
  ];
}

/**
 * Generate MEDSITREP sheets for XLSX
 * @private
 */
function generateMEDSITREPXLSX(workbook, reportData) {
  // Hospital Care Sheet
  const dhc = reportData.deployed_hospital_care || {};
  const hospitalData = [['Role', 'Location', 'Beds Total', 'Beds Occupied', 'Occupancy %', 'Status', 'Surgical Teams', 'OR Tables']];
  
  ['role2_basic', 'role2_enhanced', 'role3'].forEach(role => {
    const data = dhc[role];
    if (data) {
      hospitalData.push([
        role.replace(/_/g, ' ').toUpperCase(),
        data.location || 'N/A',
        data.beds_total || 0,
        data.beds_occupied || 0,
        data.occupancy_rate || 0,
        data.status || 'UNKNOWN',
        data.surgical_teams || 0,
        data.or_tables || 0
      ]);
    }
  });
  
  const hospitalSheet = XLSX.utils.aoa_to_sheet(hospitalData);
  hospitalSheet['!cols'] = [{ wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(workbook, hospitalSheet, 'Hospital Care');
  
  // MEDEVAC Sheet
  const ma = reportData.medevac_assets || {};
  const medevacData = [
    ['MEDEVAC Assets'],
    [''],
    ['Metric', 'Value', 'Status'],
    ['Available', ma.available || 0, ma.status || 'UNKNOWN'],
    ['Total', ma.total || 0, ''],
    ['Availability Rate', `${formatNum(ma.availability_rate, 0)}%`, ''],
    ['Missions (24h)', ma.missions_last_24h || 0, ''],
    ['Avg Response Time', ma.avg_response_time != null ? `${formatNum(ma.avg_response_time, 0)} min` : 'N/A', ''],
    ['Golden Hour Achievement', `${formatNum(ma.golden_hour_achievement, 0)}%`, '']
  ];
  
  const medevacSheet = XLSX.utils.aoa_to_sheet(medevacData);
  medevacSheet['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 15 }];
  XLSX.utils.book_append_sheet(workbook, medevacSheet, 'MEDEVAC');
  
  // Battle Casualties Sheet
  const bc = reportData.battle_casualties || {};
  const bcData = [
    ['Battle Casualties'],
    [''],
    ['Category', 'Count'],
    ['Total BC', bc.total || 0],
    ['KIA', bc.kia || 0],
    ['DOW', bc.dow || 0],
    ['WIA', bc.wia || 0],
    ['RTD', bc.rtn || 0]
  ];
  
  const bcSheet = XLSX.utils.aoa_to_sheet(bcData);
  bcSheet['!cols'] = [{ wch: 25 }, { wch: 15 }];
  XLSX.utils.book_append_sheet(workbook, bcSheet, 'Battle Casualties');
  
  // DNBI Sheet
  const dnbi = reportData.dnbi || {};
  const dnbiData = [
    ['Disease and Non-Battle Injuries'],
    [''],
    ['Metric', 'Value'],
    ['Total DNBI', dnbi.total || 0],
    ['Rate per 1000/day', dnbi.rate_per_1000 || 0]
  ];
  
  const dnbiSheet = XLSX.utils.aoa_to_sheet(dnbiData);
  dnbiSheet['!cols'] = [{ wch: 25 }, { wch: 15 }];
  XLSX.utils.book_append_sheet(workbook, dnbiSheet, 'DNBI');
}

/**
 * Generate Casualty Estimate sheets for XLSX
 * @private
 */
function generateCasualtyEstimateXLSX(workbook, reportData) {
  // Battle Casualties Sheet
  const bc = reportData.battle_casualties || {};
  const bcData = [
    ['Battle Casualties Analysis'],
    [''],
    ['Category', 'Count', 'Rate'],
    ['Total BC', bc.total || 0, `${formatNum(bc.casualty_rate, 2)}/1000/day`],
    ['KIA', bc.kia || 0, '-'],
    ['DOW', bc.dow || 0, '-'],
    ['WIA', bc.wia || 0, '-']
  ];
  
  // By unit breakdown
  if (bc.by_unit?.length > 0) {
    bcData.push(['']);
    bcData.push(['BY UNIT']);
    bcData.push(['Unit', 'Personnel', 'Total BC', 'KIA', 'DOW', 'WIA', 'Rate']);
    bc.by_unit.forEach(unit => {
      bcData.push([
        unit.unit_name || 'N/A',
        unit.personnel || 0,
        unit.total_bc || 0,
        unit.kia || 0,
        unit.dow || 0,
        unit.wia || 0,
        unit.rate || 0
      ]);
    });
  }
  
  const bcSheet = XLSX.utils.aoa_to_sheet(bcData);
  bcSheet['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(workbook, bcSheet, 'Battle Casualties');
  
  // MTF Capacity Sheet
  const mtf = reportData.mtf_capacity || {};
  const mtfData = [
    ['MTF Capacity Analysis'],
    [''],
    ['Role', 'Beds Total', 'Beds Occupied', 'Beds Available', 'Occupancy %', 'Status']
  ];
  
  ['role2_basic', 'role2_enhanced', 'role3'].forEach(role => {
    const data = mtf[role];
    if (data) {
      mtfData.push([
        data.role || role,
        data.beds_total || 0,
        data.beds_occupied || 0,
        data.beds_available || 0,
        data.occupancy_rate || 0,
        data.status || 'UNKNOWN'
      ]);
    }
  });
  
  const mtfSheet = XLSX.utils.aoa_to_sheet(mtfData);
  mtfSheet['!cols'] = [{ wch: 18 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(workbook, mtfSheet, 'MTF Capacity');
  
  // Evacuation Analysis Sheet
  const evac = reportData.evacuation_analysis || {};
  const evacData = [
    ['Evacuation Analysis'],
    [''],
    ['Metric', 'Value'],
    ['Golden Hour Achievement', `${formatNum(evac.golden_hour_achievement, 0)}%`],
    ['MEDEVAC Available', evac.effective_availability?.available || 0],
    ['MEDEVAC Required', evac.medevac_requirements?.total_helicopters || 0]
  ];
  
  const evacSheet = XLSX.utils.aoa_to_sheet(evacData);
  evacSheet['!cols'] = [{ wch: 25 }, { wch: 15 }];
  XLSX.utils.book_append_sheet(workbook, evacSheet, 'Evacuation');
}

/**
 * Generate Components Matrix sheets for XLSX
 * @private
 */
function generateComponentsMatrixXLSX(workbook, reportData) {
  const components = reportData.components || {};
  const componentNames = {
    personnel: 'Medical Personnel',
    facilities: 'Medical Treatment Facilities',
    medevac: 'Medical Evacuation',
    medlog: 'Medical Logistics',
    hns: 'Host Nation Support',
    c2: 'Command & Control'
  };
  
  const matrixData = [
    ['Components of Care Status Matrix'],
    [''],
    ['Component', 'Status', 'Percentage', 'Assessment']
  ];
  
  Object.entries(componentNames).forEach(([key, name]) => {
    const data = components[key];
    if (data) {
      matrixData.push([name, data.status || 'UNKNOWN', data.percentage || 0, data.assessment || '-']);
    }
  });
  
  // Overall
  if (components.overall) {
    matrixData.push(['']);
    matrixData.push(['OVERALL MEDICAL POSTURE', components.overall.status || 'UNKNOWN', '-', components.overall.assessment || '-']);
  }
  
  const matrixSheet = XLSX.utils.aoa_to_sheet(matrixData);
  matrixSheet['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(workbook, matrixSheet, 'Components Matrix');
}

/**
 * Generate 9-LINE MEDEVAC sheet for XLSX
 * @private
 */
function generateNineLineXLSX(workbook, reportData) {
  const lines = [
    { num: 1, desc: 'Location of Pickup Site', key: 'line_1' },
    { num: 2, desc: 'Radio Frequency', key: 'line_2' },
    { num: 3, desc: 'Number of Patients by Precedence', key: 'line_3' },
    { num: 4, desc: 'Special Equipment Required', key: 'line_4' },
    { num: 5, desc: 'Number of Patients by Type', key: 'line_5' },
    { num: 6, desc: 'Security at Pickup Site', key: 'line_6' },
    { num: 7, desc: 'Method of Marking Pickup Site', key: 'line_7' },
    { num: 8, desc: 'Patient Nationality and Status', key: 'line_8' },
    { num: 9, desc: 'NBC Contamination', key: 'line_9' }
  ];
  
  const nineLineData = [
    ['9-LINE MEDEVAC Request'],
    [''],
    ['Line', 'Description', 'Value']
  ];
  
  lines.forEach(line => {
    const value = reportData[line.key] || reportData.lines?.[line.key] || 'N/A';
    nineLineData.push([`Line ${line.num}`, line.desc, String(value)]);
  });
  
  const nineLineSheet = XLSX.utils.aoa_to_sheet(nineLineData);
  nineLineSheet['!cols'] = [{ wch: 10 }, { wch: 35 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(workbook, nineLineSheet, '9-LINE');
}

/**
 * Generate METHANE sheet for XLSX
 * @private
 */
function generateMETHANEXLSX(workbook, reportData) {
  const elements = [
    { code: 'M', desc: 'Major Incident Declaration', key: 'major_incident' },
    { code: 'E', desc: 'Exact Location', key: 'exact_location' },
    { code: 'T', desc: 'Type of Incident', key: 'type_of_incident' },
    { code: 'H', desc: 'Hazards Present', key: 'hazards' },
    { code: 'A', desc: 'Access and Egress Routes', key: 'access' },
    { code: 'N', desc: 'Number of Casualties', key: 'number_of_casualties' },
    { code: 'E', desc: 'Emergency Services Required', key: 'emergency_services' }
  ];
  
  const methaneData = [
    ['METHANE Report - Major Incident'],
    [''],
    ['Element', 'Description', 'Details']
  ];
  
  elements.forEach(el => {
    const value = reportData[el.key] || reportData.methane?.[el.key] || 'N/A';
    methaneData.push([el.code, el.desc, String(value)]);
  });
  
  const methaneSheet = XLSX.utils.aoa_to_sheet(methaneData);
  methaneSheet['!cols'] = [{ wch: 10 }, { wch: 30 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(workbook, methaneSheet, 'METHANE');
}

/**
 * Generate MEDASSESSREP sheets for XLSX
 * @private
 */
function generateMEDASSESSREPXLSX(workbook, reportData) {
  const dhc = reportData.deployed_hospital_care || {};
  const evac = reportData.evacuation || {};
  const bc = reportData.battle_casualties || {};
  
  const data = [
    ['MEDASSESSREP - Medical Assessment Report'],
    [],
    ['Assessment Period', reportData.metadata?.reporting_period || 'Weekly'],
    [],
    ['MEDICAL SITUATION'],
    ['Metric', 'Current', 'Status'],
    ['Battle Casualties', bc.total || 0, bc.status || 'GREEN'],
    ['Golden Hour %', `${evac.golden_hour_achievement || 0}%`, evac.golden_hour_achievement >= 80 ? 'GREEN' : 'AMBER'],
    ['DOW Rate', `${evac.dow_rate || 0}%`, evac.dow_status || 'GREEN'],
    [],
    ['MTF STATUS'],
    ['Facility', 'Beds', 'Occupied', 'Status'],
    ['Role 2 Basic', dhc.role2_basic?.beds_total || 0, dhc.role2_basic?.beds_occupied || 0, dhc.role2_basic?.status || 'GREEN'],
    ['Role 2 Enhanced', dhc.role2_enhanced?.beds_total || 0, dhc.role2_enhanced?.beds_occupied || 0, dhc.role2_enhanced?.status || 'GREEN'],
    ['Role 3', dhc.role3?.beds_total || 0, dhc.role3?.beds_occupied || 0, dhc.role3?.status || 'GREEN']
  ];
  
  const sheet = XLSX.utils.aoa_to_sheet(data);
  sheet['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(workbook, sheet, 'MEDASSESSREP');
}

/**
 * Generate EPINATO2 sheets for XLSX
 * @private
 */
function generateEPINATO2XLSX(workbook, reportData) {
  const dnbi = reportData.dnbi || {};
  
  const data = [
    ['EPINATO2 - Disease/Injury Surveillance Report'],
    ['IAW STANAG 2535'],
    [],
    ['Reporting Period', reportData.metadata?.reporting_period || 'Weekly'],
    [],
    ['DNBI SUMMARY'],
    ['Category', 'Cases', 'Rate/1000', 'Status'],
    ['Total DNBI', dnbi.total || 0, dnbi.rate_per_1000 || 0, dnbi.status || 'GREEN']
  ];
  
  const categories = dnbi.by_category || {};
  Object.entries(categories).forEach(([cat, catData]) => {
    data.push([cat, catData.cases || 0, catData.rate || 0, catData.status || 'GREEN']);
  });
  
  const sheet = XLSX.utils.aoa_to_sheet(data);
  sheet['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(workbook, sheet, 'EPINATO2');
}

/**
 * Generate OOSR sheets for XLSX
 * @private
 */
function generateOOSRXLSX(workbook, reportData) {
  const items = reportData.out_of_standards?.items || [];
  
  const data = [
    ['OUT OF STANDARDS REPORT (OOSR)'],
    [],
    ['Total Items Out of Standard', items.length],
    ['Critical', items.filter(i => i.severity === 'CRITICAL').length],
    ['Major', items.filter(i => i.severity === 'MAJOR').length],
    ['Minor', items.filter(i => i.severity === 'MINOR').length],
    [],
    ['DETAILED ITEMS'],
    ['Item', 'Standard', 'Actual', 'Severity', 'Action Required']
  ];
  
  items.forEach(item => {
    data.push([item.item, item.standard, item.actual, item.severity, item.action || '']);
  });
  
  if (items.length === 0) {
    data.push(['All items within NATO standards', '', '', '', '']);
  }
  
  const sheet = XLSX.utils.aoa_to_sheet(data);
  sheet['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 25 }];
  XLSX.utils.book_append_sheet(workbook, sheet, 'OOSR');
}

/**
 * Generate Blue Light sheets for XLSX
 * @private
 */
function generateBlueLightXLSX(workbook, reportData) {
  const blueLight = reportData.blue_light || {};
  const capacity = blueLight.capacity || {};
  const recommendations = blueLight.recommendations || [];
  
  const data = [
    ['BLUE LIGHT PROTOCOL REPORT'],
    [],
    ['Status', blueLight.activated ? 'ACTIVATED' : 'NOT ACTIVATED'],
    ['Trigger Reason', blueLight.trigger_reason || 'N/A'],
    ['Expected Duration', blueLight.expected_duration || 'N/A'],
    [],
    ['CAPACITY ASSESSMENT'],
    ['Resource', 'Required', 'Available', 'Gap', 'Status'],
    ['Surgical Teams', capacity.surgical_required || 0, capacity.surgical_available || 0, (capacity.surgical_required || 0) - (capacity.surgical_available || 0), capacity.surgical_status || 'GREEN'],
    ['ICU Beds', capacity.icu_required || 0, capacity.icu_available || 0, (capacity.icu_required || 0) - (capacity.icu_available || 0), capacity.icu_status || 'GREEN'],
    ['Blood Units', capacity.blood_required || 0, capacity.blood_available || 0, (capacity.blood_required || 0) - (capacity.blood_available || 0), capacity.blood_status || 'GREEN'],
    ['MEDEVAC Sorties', capacity.medevac_required || 0, capacity.medevac_available || 0, (capacity.medevac_required || 0) - (capacity.medevac_available || 0), capacity.medevac_status || 'GREEN']
  ];
  
  if (recommendations.length > 0) {
    data.push([]);
    data.push(['RECOMMENDATIONS']);
    data.push(['Priority', 'Action', 'Details']);
    recommendations.forEach(rec => {
      data.push([rec.priority || 'MEDIUM', rec.action || '', rec.details || '']);
    });
  }
  
  const sheet = XLSX.utils.aoa_to_sheet(data);
  sheet['!cols'] = [{ wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(workbook, sheet, 'Blue Light');
}

/**
 * Generate MEDEVAC Summary sheets for XLSX
 * @private
 */
function generateMEDEVACSummaryXLSX(workbook, reportData) {
  const medevac = reportData.medevac_assets || {};
  const missions = reportData.medevac_missions || {};
  const routes = medevac.routes || [];
  
  const data = [
    ['MEDEVAC SUMMARY REPORT'],
    [],
    ['ASSET AVAILABILITY'],
    ['Asset Type', 'Assigned', 'Available', 'On Mission', 'Maintenance', 'Status'],
    ['Helicopters', medevac.total || 0, medevac.available || 0, medevac.missions_last_24h || 0, (medevac.total || 0) - (medevac.available || 0) - (medevac.missions_last_24h || 0), medevac.status || 'GREEN'],
    [],
    ['MISSION STATISTICS (24H)'],
    ['Metric', 'Value'],
    ['Total Missions', missions.total || medevac.missions_last_24h || 0],
    ['Patients Evacuated', missions.patients_evacuated || 0],
    ['Golden Hour Achievement', `${medevac.golden_hour_achievement || 0}%`],
    ['Average Response Time', medevac.avg_response_time != null ? `${medevac.avg_response_time} min` : 'N/A']
  ];
  
  if (routes.length > 0) {
    data.push([]);
    data.push(['EVACUATION ROUTES']);
    data.push(['Route', 'From', 'To', 'Distance', 'Status']);
    routes.forEach(route => {
      data.push([route.name, route.from, route.to, `${route.distance} km`, route.status || 'OPEN']);
    });
  }
  
  const sheet = XLSX.utils.aoa_to_sheet(data);
  sheet['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(workbook, sheet, 'MEDEVAC Summary');
}

/**
 * Generate NATO Planning Format sheets for XLSX
 * @private
 */
function generateNATOPlanningXLSX(workbook, reportData) {
  const exec = reportData.executive_summary || {};
  const bc = reportData.battle_casualties || {};
  const components = reportData.components || {};
  
  const data = [
    ['NATO MEDICAL PLANNING FORMAT'],
    ['IAW AJMedP-5'],
    [],
    ['ANNEX Q - MEDICAL ESTIMATE'],
    [],
    ['Part 2: CASUALTY ESTIMATE'],
    ['Category', 'Estimate', 'Daily Rate'],
    ['Battle Casualties', bc.total || 0, bc.daily_rate || 0],
    ['KIA', bc.kia || 0, '-'],
    ['WIA', bc.wia || 0, '-'],
    ['DOW', bc.dow || 0, '-'],
    ['DNBI', reportData.dnbi?.total || 0, reportData.dnbi?.daily_rate || 0],
    ['TOTAL', exec.total_casualties || 0, '-'],
    [],
    ['Part 3: MEDICAL SUPPORT CONCEPT'],
    ['Component', 'Status', 'Assessment'],
    ['Medical Treatment', components.facilities?.status || 'GREEN', components.facilities?.assessment || 'Adequate'],
    ['Medical Evacuation', components.medevac?.status || 'GREEN', components.medevac?.assessment || 'Adequate'],
    ['Medical Logistics', components.medlog?.status || 'GREEN', components.medlog?.assessment || 'Adequate'],
    ['Medical Personnel', components.personnel?.status || 'GREEN', components.personnel?.assessment || 'Adequate'],
    [],
    ['Part 4: CONCLUSIONS'],
    ['Overall Supportability', components.overall?.status || 'GREEN'],
    ['Assessment', components.overall?.assessment || 'Medical support adequate for planned operations']
  ];
  
  const sheet = XLSX.utils.aoa_to_sheet(data);
  sheet['!cols'] = [{ wch: 22 }, { wch: 15 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(workbook, sheet, 'NATO Planning');
}

// ============================================================================
// MULTI-FORMAT EXPORT
// ============================================================================

/**
 * Export report in specified format
 * @param {Object} reportData - Formatted report data
 * @param {string} format - Export format
 * @param {Object} options - Export options
 * @returns {Promise<Blob>} Blob ready for download
 */
export async function exportReport(reportData, format, options = {}) {
  // CL-001: dev-only log removed
  
  const formatLower = format.toLowerCase();
  let blob;
  
  switch (formatLower) {
    case 'docx':
      blob = await exportToDOCX(reportData, options);
      break;
    case 'pdf':
      blob = await exportToPDF(reportData, options);
      break;
    case 'xlsx':
      blob = await exportToXLSX(reportData, options);
      break;
    case 'html':
      blob = exportToHTML(reportData, options);
      break;
    case 'markdown':
    case 'md':
      blob = exportToMarkdown(reportData, options);
      break;
    case 'csv':
      blob = exportToCSV(reportData, options);
      break;
    case 'json':
      blob = exportToJSON(reportData, options);
      break;
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
  
  // CL-001: dev-only log removed
  return blob;
}

/**
 * Export and download report in one operation
 * @param {Object} reportData - Formatted report data
 * @param {string} format - Export format
 * @param {Object} options - Export options
 * @returns {Promise<void>}
 */
export async function exportAndDownload(reportData, format, options = {}) {
  const blob = await exportReport(reportData, format, options);
  const filename = options.filename || generateFilename(reportData, format);
  downloadBlob(blob, filename);
}

// ============================================================================
// IMP-002: MERGED PDF EXPORT
// Combines multiple reports into a single PDF file.
// Each report gets its own cover block (title/subtitle/metadata), then its
// type-specific content, all in the same jsPDF document.
// A single unified page footer is applied at the end.
// ============================================================================

/**
 * Export multiple reports as one merged PDF document.
 * @param {Object[]} reportsArray - Array of reportData objects (from generateReportData)
 * @returns {Promise<Blob>} Single PDF Blob containing all reports in sequence
 */
export async function exportToPDFMerged(reportsArray) {
  if (!reportsArray || reportsArray.length === 0) {
    throw new Error('No reports provided for merged PDF export');
  }
  try {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth   = doc.internal.pageSize.getWidth();
    const pageHeight  = doc.internal.pageSize.getHeight();
    const margin      = 20;
    const contentWidth = pageWidth - margin * 2;

    // Adds the classification header to whatever page the doc is currently on
    const addPageHeader = (reportData) => {
      doc.setFontSize(8);
      doc.setTextColor(102, 102, 102);
      doc.text(
        reportData.metadata?.classification || 'NATO UNCLASSIFIED',
        pageWidth - margin, 10, { align: 'right' }
      );
    };

    for (let idx = 0; idx < reportsArray.length; idx++) {
      const reportData = reportsArray[idx];

      // Each report (except the first) starts on a fresh page
      if (idx > 0) {
        doc.addPage();
      }

      addPageHeader(reportData);
      let currentY = margin;

      // ---- Cover block --------------------------------------------------------
      // Report index badge
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.setFont(undefined, 'normal');
      doc.text(`Report ${idx + 1} of ${reportsArray.length}`, margin, currentY);
      currentY += 6;

      // Title
      doc.setFontSize(20);
      doc.setTextColor(25, 118, 210);
      doc.setFont(undefined, 'bold');
      doc.text(
        reportData.metadata?.report_type || 'Medical Report',
        pageWidth / 2, currentY, { align: 'center' }
      );
      currentY += 8;

      // Subtitle
      doc.setFontSize(11);
      doc.setTextColor(102, 102, 102);
      doc.setFont(undefined, 'italic');
      doc.text(reportData.metadata?.full_name || '', pageWidth / 2, currentY, { align: 'center' });
      currentY += 12;

      // Metadata table
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
      autoTable(doc, {
        startY: currentY,
        head: [],
        body: [
          ['Reporting Unit',   reportData.metadata?.reporting_unit  || 'N/A'],
          ['Date/Time Group',  reportData.metadata?.dtg             || new Date().toISOString()],
          ['Reporting Period', reportData.metadata?.reporting_period || 'N/A'],
          ['Classification',  reportData.metadata?.classification   || 'NATO UNCLASSIFIED']
        ],
        theme: 'plain',
        styles: { fontSize: 10, cellPadding: 2 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 40 },
          1: { cellWidth: contentWidth - 40 }
        },
        margin: { left: margin, right: margin }
      });
      currentY = doc.lastAutoTable.finalY + 8;

      // Thin divider between cover block and report body
      doc.setDrawColor(190, 190, 190);
      doc.setLineWidth(0.3);
      doc.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 6;

      // ---- Report-specific content -------------------------------------------
      // Generators manage their own internal currentY via doc.lastAutoTable.finalY.
      // checkPageBreak is a lightweight hint — generators call doc.addPage() via
      // autoTable overflow handling; we just need to ensure addPageHeader fires
      // on new pages added during generation.
      const makeCheckPageBreak = (startY) => {
        let y = startY;
        return (requiredSpace) => {
          if (y + requiredSpace > pageHeight - margin) {
            doc.addPage();
            addPageHeader(reportData);
            y = margin;
            return true;
          }
          return false;
        };
      };
      const checkPageBreak = makeCheckPageBreak(currentY);

      const reportType = reportData.metadata?.report_type;
      if (reportType === 'MEDSITREP') {
        generateMEDSITREPPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      } else if (reportType === 'CASUALTY_ESTIMATE') {
        generateCasualtyEstimatePDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      } else if (reportType === 'COMPONENTS_MATRIX') {
        generateComponentsMatrixPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      } else if (reportType === 'NINE_LINE' || reportType === '9-LINE MEDEVAC') {
        generateNineLinePDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      } else if (reportType === 'METHANE') {
        generateMETHANEPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      } else if (reportType === 'MEDASSESSREP') {
        generateMEDASSESSREPPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      } else if (reportType === 'EPINATO2') {
        generateEPINATO2PDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      } else if (reportType === 'OOSR') {
        generateOOSRPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      } else if (reportType === 'BLUE_LIGHT') {
        generateBlueLightPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      } else if (reportType === 'MEDEVAC_SUMMARY') {
        generateMEDEVACSummaryPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      } else if (reportType === 'NATO_PLANNING') {
        generateNATOPlanningPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      } else {
        generateGenericPDF(doc, reportData, margin, contentWidth, currentY, checkPageBreak);
      }
    }

    // ---- Unified footer on every page ----------------------------------------
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(102, 102, 102);
      doc.text(
        `PSP/CRE Tool v3.0 | Page ${i} of ${totalPages}`,
        pageWidth / 2, pageHeight - 10, { align: 'center' }
      );
      doc.text(
        `Generated: ${new Date().toISOString()}`,
        pageWidth / 2, pageHeight - 5, { align: 'center' }
      );
    }

    return doc.output('blob');
  } catch (error) {
    console.error('❌ Merged PDF export failed:', error);
    throw new Error(`Failed to generate merged PDF: ${error.message}`);
  }
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const exportUtilsModule = {
  // Export functions
  exportToHTML,
  exportToMarkdown,
  exportToCSV,
  exportToJSON,
  exportToDOCX,
  exportToPDF,
  exportToPDFMerged,
  exportToXLSX,
  exportReport,
  exportAndDownload,
  
  // Helper functions
  createBlob,
  downloadBlob,
  generateFilename,
  getStatusColor,
  formatNum,
  safeGet
};

export default exportUtilsModule;
